import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { createMeshoptGltfLoader } from '../lib/meshoptGltfLoader';
import { validatedPro100MotionOwnerIds } from '../pro100Kinematics';
import { fitSceneCameraToBox, rebuildFloor, rebuildRoomShell, rebuildModuleContactShadows, type ThreeCtx } from './useThreeScene';
import { buildHashMap, diffEntitySets, ResourceRefCounter } from './sceneReconcile';
import type { CanonicalEntity, UniversalBundle } from '@/shared/api/types';
import { api } from '@/shared/api/client';
import { bundleFrontCameraZ, cameraFitMargin, entityBox } from '../lib/geometry-helpers';
import {
  buildAlbedoCanvas,
  buildMetalnessCanvas,
  buildNormalCanvas,
  buildRoughnessCanvas,
  canvasToTexture,
} from '../lib/canvas-textures';
import {
  decorCacheKey,
  recordDecorRefusal,
  resolveDecorScan,
  type DecorScanDecision,
} from '../lib/decor-scan';
import { MATERIAL_PROFILES, type MaterialProfile } from '../types';
import { useViewerStore, type ViewerLayerMode } from '@/shared/store/viewer-store';
import { initialFurnitureObjectVisible } from '../furnitureVisibility';
import { resolveObjectProfileOrientation } from '../objectGeometryProfile';
import { FurnitureSceneEntity } from '../furnitureEntity';
import { entitiesFormApartmentScene, entityModuleOwnerId, entityObjectKind } from '../entityDomain';
import { HARDWARE_OBJECT_KINDS, normalizeObjectKind } from '../entityKinds';
import {
  buildRadiusFacadeGeometry,
  isRadiusFacadeProfile,
  type RadiusFacadeProfile,
} from '../lib/radiusGeometry';
import {
  NativeGlbScene,
  nativeGeometryContract,
  nativeGeometrySourcePolicy,
  nativeSupplementalEntities,
  nativeSourceNodeBindings,
  requiresExactNativeGeometry,
} from '../nativeGlbScene';
import { FurnitureRenderObject } from '../furnitureRenderObject';
import { setSceneFogRange, aimSceneLightsAt } from '../lib/light-rigs';
import {
  UnderCabinetLightingVisual,
  type UnderCabinetLightingVisualContract,
} from '../underCabinetLightingVisual';
import {
  ACCESSORY_ARCHETYPE_FALLBACK_FLAG,
  ACCESSORY_ARCHETYPE_PROXY_FLAG,
  applyAccessoryArchetypeMode,
  buildAccessoryArchetype,
  collectAccessoryArchetypeKits,
  indexAccessoryArchetypeKitsByMember,
  initAccessoryArchetypeMode,
} from '../accessoryArchetypes';
import { enableEmissiveBloomLayer } from '../lib/selectiveBloom';
import { InstancedBatchRegistry, type InstancedEdgeSpec } from '../lib/instancedBatches';
import { isApartmentWallDomain } from '../wallTransparency';
// PERF (render-profile 09.10): постоянные метки фаз загрузки — build/apply/ready.
import { sceneMark, sceneMeasure } from '../lib/sceneLoadMarks';

type ObjectSurfaceContract = {
  profile: MaterialProfile;
  colorHex: string;
  glassy: boolean;
  materialName: string;
  /** VFA-FE-18: the SOURCE glTF BLEND/MASK contract from cad_import. */
  sourceAlphaMode?: 'BLEND' | 'MASK' | 'OPAQUE';
  sourceAlphaCutoff?: number;
  /**
   * The catalogue article from the material passport, e.g. `EGGER/W1000-ST9-16`.
   *
   * It always arrived on `domain.material` and was dropped here: the realtime
   * branch read `profile` and `colorHex` only, so the decor scan table could
   * never be keyed and every panel fell through to a flat colour.  Empty when
   * the surface came from the `visualSurface` fallback, which carries no
   * catalogue identity at all.
   */
  article: string;
};

export function bundleOwnsRoomFloor(bundle: UniversalBundle | null | undefined): boolean {
  return (bundle?.canonicalScene?.entities ?? []).some((entity) => {
    const objectKind = entityObjectKind(entity).toLowerCase();
    const kind = String(entity.kind ?? '').trim().toLowerCase();
    return kind === 'room' || objectKind === 'room' || objectKind === 'apartment'
      || objectKind === 'building' || objectKind === 'apartment_shell'
      || objectKind === 'apartment_room';
  });
}

function recordFrom(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function profileFromContract(value: unknown): MaterialProfile | null {
  const text = String(value ?? '').trim().toLowerCase();
  return (MATERIAL_PROFILES as readonly string[]).includes(text) ? text as MaterialProfile : null;
}

function isHexColor(value: unknown): value is string {
  const text = String(value ?? '').trim();
  if (text.length !== 7 || text[0] !== '#') return false;
  for (let i = 1; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    const isDigit = code >= 48 && code <= 57;
    const isUpper = code >= 65 && code <= 70;
    const isLower = code >= 97 && code <= 102;
    if (!isDigit && !isUpper && !isLower) return false;
  }
  return true;
}

/**
 * ОТДЕЛКА ФУРНИТУРЫ — ПРЕЗЕНТАЦИЯ, И ЕЁ НЕЛЬЗЯ ЖДАТЬ ОТ КАТАЛОГА.
 *
 * Каталожная карточка описывает ЩИТ: у неё есть артикул, декор и цена. У
 * петли, ручки, ножки, штанги и полкодержателя такой карточки нет и не
 * должно быть — это покупное изделие, а не материал. Поэтому упакованный
 * каталог их отделку не несёт: замер на бандле `ШН720х450-Низ-800-2Д` —
 * щиты и фасады контракт имеют, а `hinge` 0 из 4, `handle` 0 из 2, `leg`
 * 0 из 4, `shelf_pin` 0 из 8.
 *
 * Читатель требовал контракт у ВСЕХ и молча не рисовал то, у чего его нет:
 * на показательной квартире это **1365 сущностей из 2200** и **235
 * `console.error` за один разбор сцены**. На экране шкаф оставался без
 * штанги, тумба — без ножек и ручек, и человеку об этом не говорили.
 *
 * Здесь фурнитура получает ОДНУ нейтральную металлическую отделку. Это
 * именно презентационный контракт — тот же вид, что `visualSurface`, и с
 * пустым артикулом: придумывать фурнитуре каталожный номер нельзя. Цвет
 * один на все классы намеренно: подобрать палитру «ручка чёрная, ножка
 * хромированная» — значит выдумать за человека то, чего каталог не сказал.
 * Как только каталог назовёт отделку сам, она победит — этот путь берётся
 * только когда СВОЕГО контракта нет.
 */
const HARDWARE_PRESENTATION_COLOR = '#b8bcc0';

function hardwarePresentationContract(entity: CanonicalEntity | undefined): ObjectSurfaceContract | null {
  if (!entity) return null;
  if (!HARDWARE_OBJECT_KINDS.has(normalizeObjectKind(entityObjectKind(entity)))) return null;
  const domain = recordFrom(entity.domain);
  const label = String(domain?.hardwareClass ?? domain?.hardwareRole ?? '').trim();
  return {
    profile: 'metal',
    colorHex: HARDWARE_PRESENTATION_COLOR,
    glassy: false,
    materialName: label,
    article: '',
  };
}


/**
 * РОЛЕВЫЕ ТОНАЛЬНЫЕ ИДЕНТИЧНОСТИ — премиум-дефолт сцены.
 *
 * Данные честны: и паркет (#c9bea9), и плитка (#c7c3b8), и стены (#e8e5dd)
 * — реальные палитры проекта, но все в одной светлой гамме, и сцена сливается
 * в кашу: ни пол не читается, ни стена. Премиум-рендеры решают это КОНТРАСТОМ
 * РОЛЕЙ, а не точностью палитры отделки: бетонные стены, тёплый дуб пола,
 * свой тон корпусу. Подмешиваем тон по роли поверхности; явный каталожный
 * цвет фасада неприкосновенен.
 */
function premiumRoleTone(
  entity: CanonicalEntity,
  profile: MaterialProfile,
  colorHex: string,
): string {
  const dom = recordFrom(entity.domain);
  const visualRole = String(recordFrom(dom?.visualSurface)?.role ?? '').toLowerCase();
  const entityRole = String(entity.role ?? '').toLowerCase();
  const panelKind = String(dom?.panelKind ?? '').toLowerCase();
  const out = new THREE.Color(colorHex);

  const lerpTo = (hex: string, amount: number) => out.lerp(new THREE.Color(hex), amount);

  // Стены — тёплый бетон: белая стена + белая мебель = каша; бетон даёт фон.
  if (visualRole.includes('wall')) {
    lerpTo('#9e9a90', 0.55);
    return `#${out.getHexString()}`;
  }
  // Потолок — остаётся светлым (контраст к стенам).
  if (visualRole.includes('ceiling')) return colorHex;
  // Пол-дерево — тёплый дуб: данные несут блеклый беж, дуб читается золотом.
  if (visualRole.includes('floor')) {
    if (profile === 'wood') lerpTo('#8f6a44', 0.62);
    else lerpTo('#a9a5a0', 0.28); // плитка чуть темнее и холоднее стен
    return `#${out.getHexString()}`;
  }
  // Фасад — свой заказанный цвет, не трогаем.
  if (panelKind.includes('facade') || entityRole === 'facade') return colorHex;
  // Корпус мебели — чуть глубже фасада, чтобы рёбра читались.
  if (profile === 'paint' || profile === 'wood') {
    lerpTo('#d9d6cf', 0.34);
    return `#${out.getHexString()}`;
  }
  return colorHex;
}


/**
 * Профиль поверхности по роли сущности — фоллбэк для моделей, чей билдер
 * не проставил visualSurface-контракт. Цвет берём из colorOverride/render
 * (если есть), иначе нейтральный тон профиля.
 */
function roleSurfaceProfile(entity: CanonicalEntity): ObjectSurfaceContract | null {
  const role = String(entity?.role ?? '').toLowerCase();
  const kind = String(entity?.kind ?? '').toLowerCase();
  let profile: MaterialProfile | null = null;
  if (role === 'floor') profile = 'wood';
  else if (role === 'ceiling' || role === 'skirt') profile = 'paint';
  else if (role.startsWith('wall')) profile = 'stone';   // бетон: процедурная камень-текстура
  else if (role === 'facade' || role === 'drawer_front') profile = 'paint';
  else if (['side_left', 'side_right', 'bottom', 'top', 'back', 'shelf', 'partition', 'plinth', 'plinth_rail', 'drawer_box', 'internal_drawer'].includes(role)) profile = 'wood';
  else if (role.includes('glass')) profile = 'glass';
  // Бандлы до эпохи visual_surface-контракта мебели не несут контракт:
  // выбрасывать диваны из сцены молча (рендер-аудит 03.10: пустые комнаты)
  // хуже, чем отрисовать примитив в его render.colorOverride тёплой краской.
  else if (kind === 'visual' && role === 'loose_furniture') profile = 'paint';
  // Слой инженерии/декора (visualization.layer, ТЗ Qwen #1) — кондиционеры,
  // вытяжки, козырьки, воздуховоды: render-only, вне производства. Тумблер
  // слоя скрывает их traverse'ом ПОСЛЕ попадания в сцену — без контракта
  // здесь выброс на входе оставлял тумблеру пустоту (замер 04.10: 105
  // сущностей квартиры молча не рендерились).
  else if (kind === 'visual' && role === 'visualization'
           && String(recordFrom(entity.domain)?.source) === 'visualization.layer') profile = 'appliance';
  if (!profile) return null;
  const render = entity?.render as { colorOverride?: unknown; material?: unknown } | undefined;
  const raw = String(render?.colorOverride ?? render?.material ?? '').trim();
  const colorHex = isHexColor(raw) ? raw : defaultToneForProfile(profile);
  return {
    profile,
    colorHex,
    glassy: profile === 'glass',
    materialName: `auto:${profile}:${role || kind}`,
    article: '',
  };
}

function defaultToneForProfile(profile: MaterialProfile): string {
  if (profile === 'wood') return '#9c7a52';      // дуб: полы/ЛДСП поглубже
  if (profile === 'glass') return '#cfe0e8';
  if (profile === 'stone') return '#b5b3af';      // бетон для стен (фидбек 03.10)
  return '#eceae5';                               // paint — тёплый белый
}


/**
 * GLB-загрузка покупной мебели: асинхронно заменяет процедурные боксы
 * реальными мешами (PolyHaven CC0). Активируется переключателем
 * looseFurnitureMode='glb' в настройках сцены.
 */
const glbLoaderInstance = new GLTFLoader();
const glbCache = new Map<string, THREE.Group>();

export async function loadLooseFurnitureGlb(
  glbPath: string,
  targetSize: { w: number; h: number; d: number },
  position: [number, number, number],
): Promise<THREE.Group | null> {
  const cacheKey = glbPath;
  let template = glbCache.get(cacheKey);
  if (!template) {
    try {
      const gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) => {
        glbLoaderInstance.load(glbPath, resolve, undefined, reject);
      });
      template = gltf.scene;
      glbCache.set(cacheKey, template);
    } catch {
      return null;  // файл не доступен — остаёмся на процедурных боксах
    }
  }
  const instance = template.clone(true);
  // масштабируем к AABB
  const bbox = new THREE.Box3().setFromObject(instance);
  const size = bbox.getSize(new THREE.Vector3());
  if (size.x > 0 && size.y > 0 && size.z > 0) {
    const scaleX = targetSize.w / size.x;
    const scaleY = targetSize.h / size.y;
    const scaleZ = targetSize.d / size.z;
    const scale = Math.min(scaleX, scaleY, scaleZ);
    instance.scale.setScalar(scale);
  }
  instance.position.set(position[0], position[1], position[2]);
  return instance;
}

function findProceduralEntityObject(content: THREE.Object3D, entityId: string): THREE.Object3D | null {
  let found: THREE.Object3D | null = null;
  content.traverse((candidate) => {
    if (found || candidate === content) return;
    if (candidate.userData.entityId === entityId && candidate.userData.looseFurnitureGlb !== true) {
      found = candidate;
    }
  });
  return found;
}

export function applyGlbToLooseFurniture(
  content: THREE.Object3D,
  bundle: unknown,
  enabled: boolean,
  loader: typeof loadLooseFurnitureGlb = loadLooseFurnitureGlb,
): void {
  if (!enabled || !content) return;
  const entities = (bundle as { canonicalScene?: { entities?: unknown[] } } | null)
    ?.canonicalScene?.entities;
  if (!Array.isArray(entities)) return;
  const epoch = Number(content.userData.looseFurnitureLoadEpoch ?? 0) + 1;
  content.userData.looseFurnitureLoadEpoch = epoch;
  const looseRoots = entities.filter((e) => {
    const ent = e as { kind?: unknown; role?: unknown; domain?: { glbPath?: unknown } };
    return ent?.kind === 'visual' && ent?.role === 'loose_furniture'
      && typeof ent?.domain?.glbPath === 'string';
  });
  for (const raw of looseRoots) {
    const ent = raw as {
      id?: string; domain?: { glbPath?: string; dimensionsMm?: { w?: number; h?: number; d?: number } };
      render?: { aabb?: { min?: number[]; max?: number[] } };
    };
    const glbPath = ent.domain?.glbPath;
    if (!glbPath) continue;
    const dims = ent.domain?.dimensionsMm || {};
    const aabb = ent.render?.aabb;
    const w = dims.w || (aabb?.max?.[0] ?? 0) - (aabb?.min?.[0] ?? 0) || 600;
    const h = dims.h || (aabb?.max?.[1] ?? 0) - (aabb?.min?.[1] ?? 0) || 600;
    const d = dims.d || (aabb?.max?.[2] ?? 0) - (aabb?.min?.[2] ?? 0) || 600;
    const px = aabb?.min?.[0] ?? 0;
    const py = aabb?.min?.[1] ?? 0;
    const pz = aabb?.min?.[2] ?? 0;
    const rootId = ent.id;
    if (!rootId) continue;
    loader(glbPath, { w, h, d }, [px, py, pz]).then((glb) => {
      if (glb) {
        if (content.userData.looseFurnitureLoadEpoch !== epoch) {
          disposeObject(glb);
          return;
        }
        const procGroup = findProceduralEntityObject(content, rootId);
        if (procGroup) procGroup.visible = false;
        glb.userData.looseFurnitureGlb = true;
        glb.userData.entityId = rootId;
        glb.traverse((child) => {
          child.userData.entityId = rootId;
        });
        content.add(glb);
      }
    }).catch(() => { /* остаёмся на боксах */ });
  }
}

export function objectSurfaceContract(entity: CanonicalEntity | undefined): ObjectSurfaceContract | null {
  if (!entity) return null;
  const domain = recordFrom(entity.domain);
  // ЗАКАЗНОЙ ДЕКОР ФАСАДА (приказ владельца 05.10, уход от ERP-показа):
  // source='buildspec.facade_material' ставит только buildspec-заказ, и он
  // выражает ВИДИМЫЙ тон заказа — он старше цвета governed-карточки.
  // Произвольный visualSurface (recognition-работтопы) карточку НЕ подменяет.
  const orderSurface = recordFrom(domain?.visualSurface);
  const orderProfile = profileFromContract(String(orderSurface?.profile ?? ''));
  const orderColorHex = String(orderSurface?.colorHex ?? '').trim();
  if (
    String(orderSurface?.schema ?? '') === 'holzcom.visual_surface.v1'
    && String(orderSurface?.source ?? '') === 'buildspec.facade_material'
    && orderProfile
    && isHexColor(orderColorHex)
  ) {
    return {
      profile: orderProfile,
      colorHex: orderColorHex,
      glassy: orderProfile === 'glass',
      materialName: String(orderSurface?.label ?? '').trim(),
      article: '',
    };
  }
  const material = recordFrom(domain?.material);
  const profile = profileFromContract(material?.profile);
  const colorHex = String(material?.colorHex ?? '').trim();
  // VFA-FE-18: the source alpha contract rides the imported-mesh domain.
  const sourceAlphaModeRaw = String(domain?.sourceAlphaMode ?? '').toUpperCase();
  const sourceAlphaMode: 'BLEND' | 'MASK' | 'OPAQUE' | undefined =
    sourceAlphaModeRaw === 'BLEND' || sourceAlphaModeRaw === 'MASK' || sourceAlphaModeRaw === 'OPAQUE'
      ? sourceAlphaModeRaw : undefined;
  const sourceAlphaCutoff = typeof domain?.sourceAlphaCutoff === 'number'
    ? domain.sourceAlphaCutoff : undefined;
  if (!profile || !isHexColor(colorHex)) {
    const visualSurface = recordFrom(domain?.visualSurface);
    const visualProfile = profileFromContract(visualSurface?.profile);
    const visualColorHex = String(visualSurface?.colorHex ?? '').trim();
    const visualSchema = String(visualSurface?.schema ?? '');
    const visualSource = String(visualSurface?.source ?? '').trim();
    if (
      !visualProfile
      || !isHexColor(visualColorHex)
      || !['holzcom.visual_surface.v1', 'holzcom.visual_surface_contract.v1'].includes(visualSchema)
      || !visualSource
    ) {
      // Фоллбэк ПО РОЛИ: билдер-модели не всегда штампуют материал-контракт,
      // но конвейер PBR (процедурные albedo/normal/roughness) включается
      // только через профиль. Выводим профиль из роли сущности, чтобы ЛЮБАЯ
      // модель получала материалы, а не плоский цвет (render pass 03.10).
      const roleProfile = roleSurfaceProfile(entity);
      if (!roleProfile) return null;
      return roleProfile;
    }
    return {
      profile: visualProfile,
      colorHex: visualColorHex,
      glassy: visualProfile === 'glass',
      materialName: String(visualSurface?.label ?? visualSurface?.materialKind ?? '').trim(),
      // A visual surface is a presentation contract, not a catalogue card:
      // it has no article, and inventing one would key a decor by eye.
      article: '',
    };
  }
  const facadeProfile = String(domain?.facadeProfile ?? '').trim().toLowerCase();
  return {
    profile,
    colorHex,
    glassy: profile === 'glass' || facadeProfile.startsWith('glass'),
    materialName: String(material?.name ?? '').trim(),
    article: String(material?.article ?? '').trim(),
    ...(sourceAlphaMode ? { sourceAlphaMode, ...(sourceAlphaCutoff !== undefined ? { sourceAlphaCutoff } : {}) } : {}),
  };
}

/** Shared omission policy for physical preview completeness and scene loading. */
export function canonicalGeometryOmission(entity: CanonicalEntity): 'service' | 'radius_facet' | 'hidden' | null {
  if (new FurnitureSceneEntity(entity).isServiceHelper()) return 'service';
  if (String(entity.domain?.reliefKind ?? '').trim().toLowerCase() === 'radius_facet') return 'radius_facet';
  if (entity.domain?.fixtureComponent === 'bounds' || entity.render?.visibleDefault === false) return 'hidden';
  return null;
}

export type { UnderCabinetLightingVisualContract } from '../underCabinetLightingVisual';

/**
 * A task light must be rendered from its lighting contract, never from the
 * aluminium profile's material passport.  The latter is deliberately grey,
 * which made a correctly priced 3000 K LED read as a grey fixture.
 */
export function underCabinetLightingVisualContract(
  domain: unknown,
  render: unknown,
): UnderCabinetLightingVisualContract | null {
  return UnderCabinetLightingVisual.contract(domain, render);
}

function hashSurfaceSeed(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function proceduralTextureRepeat(profile: MaterialProfile, sx: number, sy: number, sz: number): number {
  if (profile === 'glass' || profile === 'mirror') return 0;
  const meters = Math.max(0.6, Math.max(Math.abs(sx), Math.abs(sy), Math.abs(sz)) / 1000);
  const density =
    profile === 'paint' ? 1.6 :
    profile === 'wood' ? 1.15 :
    profile === 'stone' ? 0.9 :
    profile === 'metal' ? 1.35 :
    profile === 'fabric' ? 1.7 :
    1.2;
  return Math.round(Math.min(18, Math.max(1.2, meters * density)) * 10) / 10;
}

function normalStrengthFor(profile: MaterialProfile): number {
  if (profile === 'paint') return 0.9;
  if (profile === 'wood') return 1.1;
  if (profile === 'stone') return 1.8;
  if (profile === 'metal') return 1.2;
  if (profile === 'fabric') return 1.6;
  return 1.0;
}

function normalScaleFor(profile: MaterialProfile): number {
  if (profile === 'paint') return 0.18;
  if (profile === 'wood') return 0.24;
  if (profile === 'stone') return 0.34;
  if (profile === 'fabric') return 0.3;
  return 0.24;
}

/**
 * Real decor scans, loaded once per URL.
 *
 * The scan is the albedo the manufacturer actually ships; the procedural
 * normal/roughness canvases stay in place so the surface keeps its relief.
 * Colour is NOT tinted onto a scan — the scan is the colour.
 */
const decorTextureCache = new Map<string, THREE.Texture>();

function decorTexture(url: string, repeat: number): THREE.Texture {
  const key = `${url}|${repeat}`;
  const cached = decorTextureCache.get(key);
  if (cached) return cached;
  const tex = new THREE.TextureLoader().load(url);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  if (repeat > 0) tex.repeat.set(repeat, repeat);
  decorTextureCache.set(key, tex);
  return tex;
}

function buildFlatExactMaterial(
  profile: MaterialProfile,
  colorHex: string,
  glassy = false,
  textureRepeat = 0,
  materialName = '',
  decor: DecorScanDecision = { kind: 'unkeyed' },
): THREE.MeshPhysicalMaterial {
  const hasProceduralTexture = textureRepeat > 0 && !glassy && profile !== 'glass' && profile !== 'mirror';
  const hasDecorScan = decor.kind === 'scan' && !glassy && profile !== 'glass' && profile !== 'mirror';
  const mat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(hasProceduralTexture || hasDecorScan ? '#ffffff' : colorHex),
    roughness: stableRoughnessFor(profile),
    metalness: stableMetalnessFor(profile),
    envMapIntensity: stableEnvFor(profile),
    clearcoat: 0,
    side: THREE.FrontSide,
  });
  applyStablePresentationSurface(mat, profile, { glassy });
  if (hasProceduralTexture) {
    const seed = hashSurfaceSeed(`${profile}|${colorHex}|${materialName}`);
    const albedo = buildAlbedoCanvas(profile, seed, colorHex);
    mat.map = canvasToTexture(albedo, { srgb: true, repeat: textureRepeat });
    mat.normalMap = canvasToTexture(
      buildNormalCanvas(albedo, normalStrengthFor(profile)),
      { repeat: textureRepeat },
    );
    mat.roughnessMap = canvasToTexture(
      buildRoughnessCanvas(albedo, profile),
      { repeat: textureRepeat },
    );
    if (profile === 'metal' || profile === 'appliance') {
      mat.metalnessMap = canvasToTexture(
        buildMetalnessCanvas(albedo, profile),
        { repeat: textureRepeat },
      );
    }
    const normalScale = normalScaleFor(profile);
    mat.normalScale = new THREE.Vector2(normalScale, normalScale);
    mat.needsUpdate = true;
  }
  // The shipped scan replaces the synthetic albedo, and only the albedo: the
  // procedural normal/roughness above still carry the surface relief.
  if (hasDecorScan && decor.kind === 'scan') {
    mat.map = decorTexture(decor.url, textureRepeat);
    mat.needsUpdate = true;
  }
  return mat;
}

function stableRoughnessFor(profile: MaterialProfile, inside = false): number {
  const base =
    profile === 'stone' ? 0.74 :
    profile === 'metal' ? 0.38 :
    profile === 'appliance' ? 0.5 :
    profile === 'glass' ? 0.34 :
    profile === 'mirror' ? 0.12 :
    profile === 'paint' ? 0.66 :
    profile === 'wood' ? 0.78 :
    0.7;
  return inside ? Math.min(0.94, base + 0.06) : base;
}

function stableMetalnessFor(profile: MaterialProfile): number {
  if (profile === 'metal') return 0.42;
  if (profile === 'appliance') return 0.22;
  if (profile === 'mirror') return 0.5;
  return 0;
}

function stableEnvFor(profile: MaterialProfile): number {
  if (profile === 'metal') return 0.72;
  if (profile === 'appliance') return 0.58;
  if (profile === 'glass') return 0.42;
  if (profile === 'mirror') return 1.0;
  if (profile === 'stone') return 0.52;
  return 0.34;
}

function stripPresentationMaps(mat: THREE.MeshPhysicalMaterial | THREE.MeshStandardMaterial) {
  const anyMat = mat as THREE.MeshPhysicalMaterial & Record<string, unknown>;
  mat.map = null;
  mat.normalMap = null;
  mat.roughnessMap = null;
  mat.metalnessMap = null;
  mat.aoMap = null;
  mat.emissiveMap = null;
  anyMat.alphaMap = null;
  anyMat.bumpMap = null;
  anyMat.displacementMap = null;
  anyMat.clearcoatMap = null;
  anyMat.clearcoatNormalMap = null;
  anyMat.clearcoatRoughnessMap = null;
  anyMat.transmissionMap = null;
  anyMat.thicknessMap = null;
  anyMat.sheenColorMap = null;
  anyMat.sheenRoughnessMap = null;
  anyMat.specularColorMap = null;
  anyMat.specularIntensityMap = null;
}

function applyStablePresentationSurface(
  mat: THREE.MeshPhysicalMaterial | THREE.MeshStandardMaterial,
  profile: MaterialProfile,
  opts: { inside?: boolean; glassy?: boolean } = {},
) {
  stripPresentationMaps(mat);
  mat.roughness = stableRoughnessFor(profile, opts.inside);
  mat.metalness = stableMetalnessFor(profile);
  mat.envMapIntensity = stableEnvFor(profile);
  const physical = mat as THREE.MeshPhysicalMaterial;
  if ('clearcoat' in physical) physical.clearcoat = 0;
  if ('clearcoatRoughness' in physical) physical.clearcoatRoughness = 0.72;
  if (profile === 'mirror') {
    mat.color.set('#d8dde2');
  }
  if (opts.glassy || profile === 'glass') {
    applyStableGlassLook(mat);
  } else {
    applyOpaqueStableLook(mat);
  }
  mat.needsUpdate = true;
}

function applyOpaqueStableLook(mat: THREE.MeshPhysicalMaterial | THREE.MeshStandardMaterial) {
  mat.transparent = false;
  mat.opacity = 1;
  mat.depthWrite = true;
  mat.depthTest = true;
  mat.side = THREE.FrontSide;
  const anyMat = mat as THREE.MeshStandardMaterial & {
    alphaHash?: boolean;
    alphaToCoverage?: boolean;
    forceSinglePass?: boolean;
  };
  anyMat.alphaMap = null;
  anyMat.alphaTest = 0;
  anyMat.alphaHash = false;
  anyMat.alphaToCoverage = false;
  anyMat.forceSinglePass = true;
  mat.needsUpdate = true;
}

function applyStableGlassLook(mat: THREE.MeshPhysicalMaterial | THREE.MeshStandardMaterial) {
  const physical = mat as THREE.MeshPhysicalMaterial;
  mat.transparent = true;
  mat.opacity = 0.38;
  mat.depthWrite = false;
  mat.depthTest = true;
  mat.side = THREE.DoubleSide;
  const anyMat = mat as THREE.MeshStandardMaterial & {
    alphaHash?: boolean;
    alphaToCoverage?: boolean;
    forceSinglePass?: boolean;
  };
  anyMat.alphaMap = null;
  anyMat.alphaTest = 0;
  anyMat.alphaHash = false;
  anyMat.alphaToCoverage = false;
  anyMat.forceSinglePass = false;
  mat.roughness = Math.max(mat.roughness ?? 0.2, 0.28);
  mat.metalness = 0.02;
  mat.envMapIntensity = Math.min(Math.max(mat.envMapIntensity ?? 0.42, 0.34), 0.58);
  if ('transmission' in physical) physical.transmission = 0.18;
  if ('thickness' in physical) physical.thickness = 4;
  if ('ior' in physical) physical.ior = 1.45;
  mat.needsUpdate = true;
}

function cloneSceneMaterial<T extends THREE.Material>(material: T): T {
  const cloned = material.clone() as T;
  cloned.userData = { ...(material.userData || {}) };
  return cloned;
}

export type LoaderState = {
  status: 'idle' | 'loading' | 'preview' | 'ready' | 'error';
  error?: string;
  modelId?: string;
  entityIndex: Map<string, THREE.Object3D>;
};

export type SceneBoundsPayload = {
  center: THREE.Vector3;
  size: THREE.Vector3;
  cameraCenter?: THREE.Vector3;
  cameraSize?: THREE.Vector3;
};

export function useGltfLoader(
  ctx: ThreeCtx | null,
  modelId: string | null | undefined,
  bundle: UniversalBundle | undefined,
  _viewerLayerMode: ViewerLayerMode,
  onBoundsReady: (b: SceneBoundsPayload) => void,
) {
  const [state, setState] = useState<LoaderState>({
    status: 'idle',
    entityIndex: new Map(),
  });
  // Snapshot of the last committed build used by incremental reconciliation.
  // Null whenever the scene has been torn down or no build exists yet, in
  // which case reconciliation falls back to a full build.
  const prevBuildRef = useRef<PrevBuild | null>(null);
  // PERF (mesh-instancing): активный реестр инстанс-батчей — SceneCanvas
  // дёргает его тик в цикле рендера и гасит на техрежимах (xray/flat/wireframe).
  const instancingRef = useRef<InstancedBatchRegistry | null>(null);
  // Which model we've already applied the apartment wall-transparency default
  // for (so a user's manual toggle within the same model is never overridden).
  const autoWallModelRef = useRef<string | null>(null);

  // Apartment presentation defaults (à la Bazis), applied once per model so a
  // user's manual toggles are never overridden:
  //   • layer → 'tech' (Полный): the default 'body' scope is "Комната" for an
  //     apartment = SHELL ONLY, which hides every piece of furniture — the flat
  //     reads as empty rooms. Furniture lives in the furniture/tech layers.
  //
  // ПРОЗРАЧНОСТЬ СТЕН ЗДЕСЬ БОЛЬШЕ НЕ НАВЯЗЫВАЕТСЯ. Она стояла потому, что сплошная стена
  // загораживала мебель от любой внешней камеры: автопрятание было выключено (его канал не
  // видел ни одной стены — искал чужой признак). Теперь ближняя стена убирается сама
  // (`useWallAutoHide` + `wallHidesTheRoom`), и навязанное стекло только отбирало у человека
  // выбор — открывало каждую квартиру стеклянным ящиком поверх его собственной настройки.
  useEffect(() => {
    if (!modelId || !bundle) return;
    if (autoWallModelRef.current === modelId) return;
    const isApartment = entitiesFormApartmentScene(bundle.canonicalScene?.entities);
    if (!isApartment) return;
    autoWallModelRef.current = modelId;
    const store = useViewerStore.getState();
    store.setViewerLayerMode('tech');
  }, [modelId, bundle]);

  useEffect(() => {
    if (!ctx || !modelId || bundle) return;
    const group = ctx.content;
    instancingRef.current?.dispose();
    instancingRef.current = null;
    while (group.children.length) {
      const c = group.children.pop()!;
      disposeObject(c);
    }
    // Scene content was wiped — invalidate the incremental snapshot so the
    // next bundle always takes the full path, and forget all shared-resource
    // counts (disposeObject already freed the GPU resources wholesale).
    prevBuildRef.current = null;
    sharedResources.clear();
    setState({ status: 'loading', modelId, entityIndex: new Map() });
  }, [ctx, modelId, bundle]);

  useEffect(() => {
    if (!ctx || !modelId || !bundle) return;

    // Reuse the standing scene graph and rebuild only entities whose render
    // contract changed. Any uncertainty returns `fallback` and takes the full
    // rebuild path below. This is normal product behaviour, not a runtime flag.
    const nextEntities = bundle.canonicalScene?.entities ?? [];
    const result = tryIncrementalRebuild(
      ctx,
      modelId,
      bundle,
      prevBuildRef.current,
      nextEntities,
    );
    if (result.outcome !== 'fallback') {
      prevBuildRef.current = result.prevBuild;
      // 'noop' → scene + React state unchanged, avoid a re-render entirely.
      // 'applied' → only changed entities were swapped; camera untouched.
      if (result.outcome === 'applied') setState(result.state);
      return;
    }
    // fall through to the full rebuild.

    let cancelled = false;
    const timeoutHandle: number | null = null;
    const clearContent = () => {
      const group = ctx.content;
      group.userData.looseFurnitureLoadEpoch = Number(group.userData.looseFurnitureLoadEpoch ?? 0) + 1;
      instancingRef.current?.dispose();
      instancingRef.current = null;
      while (group.children.length) {
        const c = group.children.pop()!;
        disposeObject(c);
      }
      // Full teardown frees every GPU resource wholesale via disposeObject —
      // reset the fast-path refcounts so no stale count survives into the next
      // build (which allocates fresh resource instances anyway).
      sharedResources.clear();
    };
    clearContent();
    setState({ status: 'loading', modelId, entityIndex: new Map() });

    const rawSourceKind = (bundle as unknown as { sourceKind?: unknown }).sourceKind;
    const sourceKind = typeof rawSourceKind === 'string' ? rawSourceKind.toLowerCase() : '';
    const nativeSourcePolicy = nativeGeometrySourcePolicy(bundle);
    const nativeContract = nativeGeometryContract(bundle);
    if (nativeSourcePolicy === 'invalid' || (requiresExactNativeGeometry(bundle) && !nativeContract)) {
      setState({
        status: 'error',
        error: 'PRO100 native geometry binding contract is missing or invalid',
        modelId,
        entityIndex: new Map(),
      });
      return;
    }
    // Apartment scenes carry their own walls / floor / ceiling — the
    // decorative backdrop (gray L-wall behind scene) doubles up and shows
    // through as «белые артефакты вне стен».  Suppress it when the bundle
    // has object-owned apartment entities.
    const isApartmentBundle = bundleOwnsRoomFloor(bundle);
    // A room bundle already owns its exact floor geometry.  The studio plane is
    // only a backdrop for an isolated object; showing both produces two floors
    // and, worse, gives drag raycasts two competing landing surfaces.
    ctx.floorGroup.visible = !isApartmentBundle;
    const showRoomShell = sourceKind === 'pdf' && !isApartmentBundle;
    const applyScene = (
      root: THREE.Object3D,
      index: Map<string, THREE.Object3D>,
      status: 'preview' | 'ready',
      opts?: { fitCamera?: boolean; emitBounds?: boolean; rebuildFloorNow?: boolean; registry?: InstancedBatchRegistry | null },
    ) => {
      if (cancelled) {
        disposeObject(root);
        opts?.registry?.dispose();
        return;
      }
      clearContent();
      instancingRef.current = opts?.registry ?? null;
      ctx.content.add(root);
      const sceneEpoch = Number(ctx.content.userData.looseFurnitureLoadEpoch ?? 0);
      // GLB-покупная: при режиме 'glb' заменяем процедурные боксы реальными
      // мешами (PolyHaven CC0). Асинхронно — сцена уже доступна.
      // прямой вызов — функции определены в этом же файле
      import('@/shared/store/viewer-store').then(({ useViewerStore }) => {
        if (cancelled || Number(ctx.content.userData.looseFurnitureLoadEpoch ?? 0) !== sceneEpoch) return;
        const mode = useViewerStore.getState().looseFurnitureMode;
        if (mode === 'glb') {
          applyGlbToLooseFurniture(ctx.content, bundle, true);
        }
        // Тумблер видимости слоя инженерии/декора (ТЗ Qwen #1)
        const visVisible = useViewerStore.getState().visualizationLayerVisible;
        if (!visVisible) {
          root.traverse((obj) => {
            const dom = (obj.userData as { domain?: { source?: string } }).domain;
            if (dom?.source === 'visualization.layer') {
              obj.visible = false;
            }
          });
        }
      }).catch(() => {/* стора нет — остаёмся на боксах */});
      const box = new THREE.Box3().setFromObject(root);
      const validBox = !box.isEmpty() && Number.isFinite(box.min.x) && Number.isFinite(box.max.x);
      if (validBox) {
        if (opts?.rebuildFloorNow !== false) rebuildFloor(ctx, box);
        // Контакт «пол ↔ цоколь» под каждым модулем (P0-4 render-audit)
        rebuildModuleContactShadows(ctx, root);
        rebuildRoomShell(ctx, box, { enabled: showRoomShell });
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        // Свет и тени обязаны знать, ГДЕ стоит сцена: key-light по умолчанию
        // целится в мировые нули, и у квартиры в 21 метр от них тени не
        // рисуются вовсе, а рендер читается плоским.
        aimSceneLightsAt(ctx.scene, box);
        const maxDim = Math.max(size.x, size.y, size.z);
        if (showRoomShell) {
          const fogNear = Math.max(14000, maxDim * 4.4);
          const fogFar = Math.max(26000, maxDim * 8.6);
          setSceneFogRange(ctx.scene, fogNear, fogFar);
        } else {
          // Scale fog with scene size — hardcoded 9k–18k clipped large
          // (apartment/floor-plan) scenes since the fit camera ends up well
          // past the fog far. Use maxDim * (2, 5) with the current floor plane so
          // small-cabinet depth haze is preserved but apartments stay clear.
          const fogNear = Math.max(9000, maxDim * 2.0);
          const fogFar = Math.max(18000, maxDim * 5.0);
          setSceneFogRange(ctx.scene, fogNear, fogFar);
        }
        // Navigation and evidence cameras deliberately use different envelopes.
        // Orbit navigation benefits from a little breathing room, while a
        // catalog/evidence render must frame the furniture itself rather than
        // the floor-margin used to make mouse navigation comfortable.
        const productBox = nativeContract
          ? box.clone()
          : productBoxForBundle(bundle, box, { isApartmentBundle, showRoomShell });
        const cameraBox = nativeContract
          ? box.clone()
          : initialCameraBoxForBundle(bundle, box, { isApartmentBundle, showRoomShell });
        const cameraSize = productBox.getSize(new THREE.Vector3());
        const cameraCenter = productBox.getCenter(new THREE.Vector3());
        if (opts?.fitCamera) {
          // cameraFitMargin, not cameraFramePadding: fitCameraToBox now returns
          // an exact fit, so it needs a small margin rather than the large
          // multiplier that used to compensate for bounding-sphere framing.
          const framePadding = nativeContract ? 1.04 : cameraFitMargin(showRoomShell);
          // Не `fitCameraToBox` напрямую: модель может грузиться при УЖЕ
          // включённом плоском виде, и тогда активна ортографическая камера —
          // перспективная подгонка отодвинула бы её, не изменив кадр.
          fitSceneCameraToBox(ctx, cameraBox, framePadding, new THREE.Vector3(1, 0.75, bundleFrontCameraZ(bundle)));
        }
        if (opts?.emitBounds) onBoundsReady({ center, size, cameraCenter, cameraSize });
      } else {
        rebuildRoomShell(ctx, box, { enabled: false });
      }
      setState({ status, modelId, entityIndex: index });
    };

    if (nativeContract) {
      if (bundle.sourceKind === 'pro100_decode') validatedPro100MotionOwnerIds(bundle);
      // Опубликованный GLB приходит сжатым (EXT_meshopt_compression от
      // SKP-воркера); декодер в хелпере безвреден и для старых несжатых
      // бандлов — формат конкретной модели определяется её артефактом.
      const nativeLoader = createMeshoptGltfLoader();
      nativeLoader.load(
        // Версионный sha в URL включает immutable-кэш на бэкенде: повторное
        // открытие опубликованной сцены не перекачивает десятки мегабайт GLB.
        api.glbUrl(modelId, nativeContract.artifactSha256),
        async (gltf) => {
          if (cancelled) {
            disposeObject(gltf.scene);
            return;
          }
          try {
            const sourceBindings = await nativeSourceNodeBindings(gltf);
            if (cancelled) { disposeObject(gltf.scene); return; }
            const prepared = new NativeGlbScene(gltf.scene, bundle, nativeContract, sourceBindings).prepare();
            if (prepared.unmatchedMeshes > 0 || prepared.matchedParts !== nativeContract.partCount) {
              throw new Error(
                `GLB entity binding mismatch: matchedParts=${prepared.matchedParts}, primitives=${prepared.matchedMeshes}, `
                + `unmatched=${prepared.unmatchedMeshes}, expected=${nativeContract.partCount}`,
              );
            }
            const previewEntities = nativeSupplementalEntities(bundle);
            if (previewEntities.some((entity) => {
              const domain = entity.domain && typeof entity.domain === 'object'
                ? entity.domain as Record<string, unknown> : {};
              return domain.graph3Generated === true
                || domain.materializedBy === 'pro100.typed-catalog-replacement.v1'
                || previewEntities.some((root) => root.id === entity.moduleId
                  && (root.domain as Record<string, unknown> | undefined)?.materializedBy
                    === 'pro100.typed-catalog-replacement.v1');
            })) {
              const previewBundle = {
                ...bundle,
                sourceKind: 'graph3_preview',
                canonicalScene: { ...bundle.canonicalScene, entities: previewEntities },
              } as UniversalBundle;
              const preview = buildFromCanonical(previewBundle);
              preview.group.name = 'reviewed-graph3-preview';
              prepared.root.add(preview.group);
              for (const [entityId, object] of preview.index) prepared.index.set(entityId, object);
            }
            const meshes: THREE.Mesh[] = [];
            prepared.root.traverse((object) => {
              applyAccessoryArchetypeMode(object, useViewerStore.getState().renderMode);
              const mesh = object as THREE.Mesh;
              if (!mesh.isMesh) return;
              meshes.push(mesh);
              new FurnitureRenderObject(mesh).applyMaterialMode(useViewerStore.getState().renderMode);
            });
            buildMeshBvhs(meshes);
            applyScene(prepared.root, prepared.index, 'ready', {
              fitCamera: true,
              emitBounds: true,
              rebuildFloorNow: true,
            });
            prevBuildRef.current = null;
          } catch (error) {
            disposeObject(gltf.scene);
            setState({
              status: 'error',
              error: error instanceof Error ? error.message : String(error),
              modelId,
              entityIndex: new Map(),
            });
          }
        },
        undefined,
        (error) => {
          if (cancelled) return;
          setState({
            status: 'error',
            error: error instanceof Error ? error.message : String(error),
            modelId,
            entityIndex: new Map(),
          });
        },
      );
      return () => {
        cancelled = true;
      };
    }

    sceneMark('scene:build-start');
    sceneMark('scene:build-meshes-start');
    let canonical: ReturnType<typeof buildFromCanonical>;
    try {
      canonical = buildFromCanonical(bundle, true);
    } catch (error) {
      setState({
        status: 'error',
        error: error instanceof Error ? error.message : String(error),
        modelId,
        entityIndex: new Map(),
      });
      return;
    }
    sceneMark('scene:build-meshes-end');
    sceneMeasure('scene:build-meshes', 'scene:build-meshes-start', 'scene:build-meshes-end');
    sceneMark('scene:apply-start');
    applyScene(canonical.group, canonical.index, 'ready', {
      fitCamera: true,
      emitBounds: true,
      rebuildFloorNow: true,
      registry: canonical.registry,
    });
    sceneMark('scene:apply-end');
    sceneMeasure('scene:apply', 'scene:apply-start', 'scene:apply-end');
    sceneMark('scene:ready');
    sceneMeasure('scene:load-total', 'scene:build-start', 'scene:ready');
    // Record the committed build so a later bundle change can attempt an
    // incremental rebuild.  `applyScene` runs synchronously here with
    // `cancelled === false`, so `canonical.group` is attached under ctx.content.
    // `clearContent` (inside applyScene) reset the counter to empty just before
    // this, so retaining the whole committed tree now establishes exactly one
    // count per live mesh-reference — the baseline the fast path decrements.
    if (!cancelled) {
      retainSharedResources(canonical.group);
      prevBuildRef.current = {
        modelId,
        hashById: buildHashMap(bundle.canonicalScene.entities || []),
        index: canonical.index,
        rootGroup: canonical.group,
        renderedChildModuleIds: canonical.renderedChildModuleIds,
        registry: canonical.registry,
      };
    }

    return () => {
      cancelled = true;
      if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
    };
    // viewerLayerMode intentionally NOT in deps — changing it must NOT rebuild
    // the entire scene (674 meshes + materials = 1-2s freeze).  Visibility
    // toggle lives in SceneCanvas → applyLayerVisibility effect.
  }, [ctx, modelId, bundle, onBoundsReady]);

  return { ...state, instancingRef };
}

/**
 * Camera framing box. Best-in-class furniture viewers frame the FURNITURE, not
 * the empty room/apartment envelope — so we fit the union of the visible module
 * boxes (with padding) for BOTH apartment and furniture-only scenes. The old
 * behaviour returned the full scene box for apartments, which framed the ~23 m
 * apartment envelope and left the actual cabinets tiny / seemingly off-screen.
 * Falls back to the full box when there are no modules to frame.
 */
function initialCameraBoxForBundle(
  bundle: UniversalBundle | undefined,
  fullBox: THREE.Box3,
  _opts: { isApartmentBundle: boolean; showRoomShell: boolean },
): THREE.Box3 {
  // Breathing room is the camera margin's job (cameraFitMargin), not a
  // world-space cushion here.  Expanding by a fixed 700 mm meant the margin
  // scaled with nothing: it barely showed on a 6 m run and more than tripled
  // the framed footprint of a 600 mm wardrobe, which is why furniture kept
  // landing marooned in the middle of the viewport.
  return productBoxForBundle(bundle, fullBox, _opts);
}

/**
 * Tight physical furniture envelope for deterministic product photography.
 * This deliberately excludes the extra orbit-camera margin from
 * `initialCameraBoxForBundle` — otherwise an L/P kitchen is rendered as a
 * tiny object floating in a large floor plane.
 */
function productBoxForBundle(
  bundle: UniversalBundle | undefined,
  fullBox: THREE.Box3,
  _opts: { isApartmentBundle: boolean; showRoomShell: boolean },
): THREE.Box3 {
  if (!bundle) return fullBox;
  // A genuine apartment (multi-room shell): frame the WHOLE flat envelope, not
  // just the furniture clusters. Fitting the furniture union here crops far
  // rooms (гостиная/балкон) out of view and reads as "half the flat is
  // missing". The furniture-union heuristic below is for single-room furniture
  // scenes where the room envelope would dwarf the cabinets.
  if (_opts.isApartmentBundle) return fullBox;
  const entities = bundle.canonicalScene?.entities ?? [];
  const moduleBoxes = entities
    .filter((entity) => new FurnitureSceneEntity(entity).isModule())
    .map((entity) => entityBox(entity))
    .filter((box): box is THREE.Box3 => Boolean(box && !box.isEmpty()));
  if (moduleBoxes.length < 1) return fullBox;

  // Union of all furniture modules → the content the user actually wants to see.
  const furniture = new THREE.Box3();
  for (const box of moduleBoxes) furniture.union(box);
  if (furniture.isEmpty()) return fullBox;

  return furniture;
}

const _bvhGeomMarkers = new WeakSet<THREE.BufferGeometry>();
function buildMeshBvhs(meshes: THREE.Mesh[]) {
  for (const m of meshes) {
    const g = m.geometry as THREE.BufferGeometry | undefined;
    if (!g) continue;
    if (_bvhGeomMarkers.has(g)) continue;
    // Skip empty/non-indexed buffers — three-mesh-bvh handles non-indexed too,
    // but tiny geometries don't benefit and waste time.
    const posAttr = g.getAttribute('position');
    if (!posAttr || posAttr.count < 16) continue;
    try {
       
      (g as any).computeBoundsTree?.();
      _bvhGeomMarkers.add(g);
    } catch {
      // ignore — Three.js default raycast remains valid
    }
  }
}

/**
 * Shared build context for a bundle.  Produces a per-entity builder
 * (`buildEntityObject`) plus the cross-entity inputs the incremental fast path
 * needs to reason about identity (`entitiesById`, `renderedChildModuleIds`,
 * `maxCanonicalMeshes`).
 *
 * IMPORTANT: `buildFromCanonical` (the full path) drives this exact builder in
 * a plain loop, so the full-rebuild output is byte-for-byte identical to the
 * pre-refactor behaviour.  The incremental path (see useGltfLoader) reuses the
 * SAME builder with the SAME full-bundle context, so any entity it rebuilds is
 * produced identically to how a full rebuild would produce it.
 */
type CanonicalBuilder = {
  entities: CanonicalEntity[];
  entitiesById: Map<string, CanonicalEntity>;
  /** Module ids that own at least one rendered child (→ module envelope is suppressed). */
  renderedChildModuleIds: Set<string>;
  maxCanonicalMeshes: number;
  /**
   * ВОЛНА P: сущности, входящие в архетипные киты аксессуаров.  Правка любой
   * из них меняет объединённый AABB кита — инкрементальный путь такое не
   * выражает (архетип строится по всему киту), поэтому просит полный ребилд.
   */
  accessoryArchetypeMemberIds: ReadonlySet<string>;
  /** Build the top-level Object3D for one entity, or null if it emits nothing. */
  buildEntityObject: (e: CanonicalEntity) => THREE.Object3D | null;
};

export function assertCanonicalSceneCapacity(renderedCount: number, maxCanonicalMeshes: number): void {
  if (renderedCount >= maxCanonicalMeshes) {
    throw new Error(`CANONICAL_SCENE_TOO_LARGE: renderable entities exceed ${maxCanonicalMeshes}`);
  }
}

export function incrementalSceneMayExceedCapacity(
  currentRenderedCount: number,
  potentialNewObjects: number,
  maxCanonicalMeshes: number,
): boolean {
  return currentRenderedCount + potentialNewObjects > maxCanonicalMeshes;
}

/**
 * PERF (mesh-instancing): контракт режима совместим с инстансингом, если его
 * SOLID-состояние (по правилам materialStateForMode) совпадает с дефолтом, а
 * renderOrder — конечное число (он уходит в ключ бакета и в renderOrder меша
 * бакета). Материал бакета в solid мутируется ровно к этому дефолту, значит
 * члены неотличимы. Техрежимы (xray/wireframe/flat) выводят инстансинг в
 * суспенз целиком, поэтому их override-ветки контракта безопасны.
 */
function renderModeContractIsInstancingSafe(contract: unknown): boolean {
  if (contract === null || contract === undefined) return true;
  if (typeof contract !== 'object' || Array.isArray(contract)) return false;
  const rec = contract as Record<string, unknown>;
  const finite = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);
  // Тот же порядок разрешения, что в FurnitureEntityVisibility.materialStateForMode:
  // per-mode override перекрывает верхний уровень, верхний — дефолт.
  const pick = (key: string): unknown => {
    const solid = rec.solid;
    if (solid && typeof solid === 'object' && !Array.isArray(solid)
      && key in (solid as Record<string, unknown>)) {
      return (solid as Record<string, unknown>)[key];
    }
    return rec[key];
  };
  const opacityRaw = pick('opacity');
  const opacity = finite(opacityRaw) ? opacityRaw : 1;
  const transparentRaw = pick('transparent');
  const transparent = typeof transparentRaw === 'boolean' ? transparentRaw : opacity < 1;
  const depthWriteRaw = pick('depthWrite');
  const depthWrite = typeof depthWriteRaw === 'boolean' ? depthWriteRaw : opacity >= 1;
  const wireframe = pick('wireframe') === true;
  const renderOrderRaw = rec.renderOrder;
  const renderOrderOk = renderOrderRaw === undefined || finite(renderOrderRaw);
  return opacity === 1 && transparent === false && depthWrite === true && wireframe === false
    && renderOrderOk;
}

function createCanonicalBuilder(
  bundle: UniversalBundle,
  instancing?: InstancedBatchRegistry | null,
): CanonicalBuilder {
  const entities = bundle.canonicalScene.entities || [];
  const entitiesById = new Map<string, CanonicalEntity>();
  for (const e of entities) entitiesById.set(e.id, e);
  // Production furniture must arrive as object-owned panels/facades/hardware.
  // The renderer does not invent cabinet internals from module envelopes.
  const renderedChildrenByModuleId = new Map<string, number>();
  for (const entity of entities) {
    const sceneEntity = new FurnitureSceneEntity(entity);
    if (!sceneEntity.isModuleRenderChild()) continue;
    if (!entity.render?.size || !entity.render?.center) continue;
    const moduleId = entityModuleOwnerId(entity, entitiesById);
    if (!moduleId) continue;
    renderedChildrenByModuleId.set(moduleId, (renderedChildrenByModuleId.get(moduleId) ?? 0) + 1);
  }
  const heavyScene = entities.length > 3500;
  // A hard budget remains a fail-closed safety boundary. It must never turn
  // into a partial "ready" scene; callers receive CANONICAL_SCENE_TOO_LARGE.
  const maxCanonicalMeshes = 9000;

  const geometryCache = new Map<string, THREE.BufferGeometry>();
  const materialCache = new Map<string, THREE.MeshPhysicalMaterial>();
  const textureCache = new Map<string, THREE.Texture>();
  const edgeGeometryCache = new Map<string, THREE.BufferGeometry>();
  const getBoxGeometry = (key: string, sx: number, sy: number, sz: number) => {
    let geom = geometryCache.get(key);
    if (!geom) {
      geom = new THREE.BoxGeometry(sx, sy, sz);
      geometryCache.set(key, geom);
    }
    return geom;
  };
  // Radius (curved) facades: a smooth arc-bowed slab instead of a flat box.
  // Cached by dimensions + profile like every other geometry so identical
  // facades share one BufferGeometry (and one BVH).
  const getRadiusFacadeGeometry = (
    key: string,
    sx: number,
    sy: number,
    sz: number,
    profile: RadiusFacadeProfile,
  ) => {
    let geom = geometryCache.get(key);
    if (!geom) {
      geom = buildRadiusFacadeGeometry([sx, sy, sz], profile, heavyScene ? 16 : 24);
      geometryCache.set(key, geom);
    }
    return geom;
  };
  const getImagePlaneGeometry = (key: string, sx: number, sz: number) => {
    let geom = geometryCache.get(key);
    if (!geom) {
      geom = new THREE.PlaneGeometry(Math.max(1, sx), Math.max(1, sz));
      geom.rotateX(-Math.PI / 2);
      geometryCache.set(key, geom);
    }
    return geom;
  };
  const getImageTexture = (url: string) => {
    let texture = textureCache.get(url);
    if (!texture) {
      texture = new THREE.TextureLoader().load(url);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 4;
      textureCache.set(url, texture);
    }
    return texture;
  };
  // (#2) Round and oval parts — carousel discs/posts and sink bowls set
  // render.primitive='cylinder'.  X and Z are independent diameters: using
  // max(sx, sz) for both axes silently turned every oval sink into a circle.
  const getCylinderGeometry = (key: string, sx: number, sy: number, sz: number) => {
    let geom = geometryCache.get(key);
    if (!geom) {
      geom = new THREE.CylinderGeometry(0.5, 0.5, 1, heavyScene ? 16 : 28);
      geom.scale(Math.max(1, sx), Math.max(1, sy), Math.max(1, sz));
      geometryCache.set(key, geom);
    }
    return geom;
  };
  /* (#7-скульпт) Ручка-скоба вместо бруса. Источник габаритов тот же, что у
   * бокса: render.size ручки — П-контур по самой длинной оси плюс диаметр
   * трубы по тончайшей. Бэкенд кладёт bbox ручки СНАРУЖИ от фасада
   * (module_hardware_builder: [facade_z, facade_z + proud]) с центром в
   * середине — контур обязан занимать ту же половину: ноги у фасадной
   * грани (−depth/2), перемычка наружу (+depth/2 − радиус). Геометрия
   * кэшируется и живёт в общем dispose-цикле. */
  const getHandleSweepGeometry = (key: string, sx: number, sy: number, sz: number) => {
    let geom = geometryCache.get(key);
    if (!geom) {
      const axes: [number, number, number] = [sx, sy, sz];
      const longAxis = axes.indexOf(Math.max(...axes));
      const thinAxis = axes.indexOf(Math.min(...axes));
      const length = Math.max(...axes);
      const depth = axes[3 - longAxis - thinAxis];
      const diameter = Math.max(4, Math.min(axes[thinAxis], 14));
      const radius = diameter / 2;
      const straight = Math.max(1, length / 2 - radius);
      const point = (along: number, out: number): THREE.Vector3 => {
        const p = [0, 0, 0];
        p[longAxis] = along;
        p[thinAxis] = 0;
        p[3 - longAxis - thinAxis] = out;
        return new THREE.Vector3(p[0], p[1], p[2]);
      };
      // out: 0 = центр bbox (меш позиционируется центром, как бокс до него).
      // Фасадная грань — это −depth/2: ноги стоят на ней, перемычка
      // отогнута наружу, но НЕ выходит за +depth/2 — габарит обещан бэком.
      const mount = -depth / 2 + radius * 0.2;
      const bar = Math.min(depth / 2 - radius, mount + radius);
      const curve = new THREE.CatmullRomCurve3([
        point(-straight, mount),
        point(-straight, (mount + bar) * 0.5),
        point(-straight * 0.97, bar + (mount - bar) * 0.25),
        point(-straight * 0.9, bar),
        point(0, bar),
        point(straight * 0.9, bar),
        point(straight * 0.97, bar + (mount - bar) * 0.25),
        point(straight, (mount + bar) * 0.5),
        point(straight, mount),
      ], false, 'catmullrom', 0.12);
      geom = new THREE.TubeGeometry(curve, heavyScene ? 24 : 48, radius, heavyScene ? 8 : 12, false);
      geometryCache.set(key, geom);
    }
    return geom;
  };
  const materialForHardwareProfile = (key: string, color: string) => {
    const safeColor = isHexColor(color) ? color : null;
    if (!safeColor) return null;
    const mKey = `hardware-profile|${safeColor}`;
    let mat = materialCache.get(mKey);
    if (!mat) {
      mat = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(safeColor),
        roughness: 0.46,
        metalness: 0.58,
        envMapIntensity: 0.82,
        transparent: false,
        opacity: 1,
        depthWrite: true,
        depthTest: true,
        side: THREE.FrontSide,
      });
      mat.name = key;
      materialCache.set(mKey, mat);
    }
    return mat;
  };
  const getEdgeGeometry = (key: string, geometry: THREE.BufferGeometry, thresholdDeg = 1) => {
    let edgeGeometry = edgeGeometryCache.get(key);
    if (!edgeGeometry) {
      edgeGeometry = new THREE.EdgesGeometry(geometry, thresholdDeg);
      edgeGeometryCache.set(key, edgeGeometry);
    }
    return edgeGeometry;
  };
  const attachBasisEdgeOverlay = (
    mesh: THREE.Mesh,
    entity: CanonicalEntity,
    geometryKey: string,
    opts: { hardware?: boolean; glass?: boolean; cylinder?: boolean; edgeThresholdDeg?: number } = {},
  ) => {
    // Radius facades: a low threshold would trace every arc facet fold (a grid
    // of lines on the curve).  A high threshold keeps only the sharp perimeter
    // corners, so the curved panel gets a clean outline like a flat one.
    const edgeGeometry = getEdgeGeometry(`edge|${geometryKey}`, mesh.geometry, opts.edgeThresholdDeg ?? 1);
    // Рёбра железа: почти-чёрные жирные линии съедали светлый хром-меш на
    // мелких размерах (полкодержатели/навесы читались чёрными плашками на
    // светлом ХДФ). Линии остаются для силуэта, но тихими — меш #b8bcc0
    // владеет видом, а не обводка.
    const color = opts.hardware ? 0x6c7480 : opts.glass ? 0x334155 : 0x15181d;
    const material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: opts.hardware ? 0.38 : 0.62,
      depthTest: true,
      depthWrite: false,
      toneMapped: false,
    });
    const lines = new THREE.LineSegments(edgeGeometry, material);
    lines.name = `${entity.id}::basis_edges`;
    lines.renderOrder = (mesh.renderOrder || 0) + (opts.hardware ? 4 : 2);
    lines.userData.basisEdgeOverlay = true;
    lines.userData.edgeHardware = Boolean(opts.hardware);
    lines.userData.edgeGlass = Boolean(opts.glass);
    lines.userData.edgeCylinder = Boolean(opts.cylinder);
    lines.userData.entityId = entity.id;
    if (entity.name) lines.userData.name = entity.name;
    if (entity.render) lines.userData.render = entity.render;
    if ((entity as CanonicalEntity & { domain?: unknown }).domain) {
      lines.userData.domain = (entity as CanonicalEntity & { domain?: unknown }).domain;
    }
    const mid = entityModuleOwnerId(entity, entitiesById);
    if (mid) lines.userData.moduleId = mid;
    mesh.add(lines);
  };
  const buildObjectGeometryProfile = (
    entity: CanonicalEntity,
    center: [number, number, number],
    size: [number, number, number],
    quaternion: number[] | undefined,
    opts?: { allowInstancing?: boolean },
  ): THREE.Object3D | null => {
    const domain = (entity as CanonicalEntity & { domain?: Record<string, unknown> }).domain;
    const geometryProfile = domain?.geometryProfile as {
      parts?: unknown[];
      profileId?: string;
      backWall?: string;
    } | undefined;
    const hardwareProfile = domain?.visualProfile as { parts?: unknown[]; profileId?: string } | undefined;
    const profile = geometryProfile ?? hardwareProfile;
    const isHardwareProfile = !geometryProfile && Boolean(hardwareProfile);
    const parts = Array.isArray(profile?.parts) ? profile.parts : [];
    if (!parts.length) return null;
    const group = new THREE.Group();
    group.position.set(center[0], center[1], center[2]);
    if (quaternion && quaternion.length >= 4) group.quaternion.set(quaternion[0], quaternion[1], quaternion[2], quaternion[3]).normalize();
    const orientation = resolveObjectProfileOrientation(size, geometryProfile?.backWall);
    const localSize = orientation.localSize;
    if (orientation.facingYRad) group.rotateY(orientation.facingYRad);
    const moduleId = entityModuleOwnerId(entity, entitiesById);
    const stampHardwareObject = (object: THREE.Object3D) => {
      object.userData.entityId = entity.id;
      if (entity.name) object.userData.name = entity.name;
      if (entity.render) object.userData.render = entity.render;
      if ((entity as CanonicalEntity & { domain?: unknown }).domain) {
        object.userData.domain = (entity as CanonicalEntity & { domain?: unknown }).domain;
      }
      if (moduleId) object.userData.moduleId = moduleId;
    };
    stampHardwareObject(group);

    // PERF (mesh-instancing): hardware-профили (ножки/полкодержатели/петли/
    // направляющие — 8 профилей на ~2k частей в большой сцене) — самый
    // массовый класс мешей. Части объекта уезжают в инстанс-бакеты ЦЕЛИКОМ
    // (all-or-nothing): любая непрозрачная часть-исключение возвращает объект
    // на классический путь. Подготовка одна, сборка — инстансная или
    // классическая, чтобы рецепты не расходились с классикой.
    const entityStamp: Record<string, unknown> = { entityId: entity.id };
    if (entity.name) entityStamp.name = entity.name;
    if (entity.render) entityStamp.render = entity.render;
    if ((entity as CanonicalEntity & { domain?: unknown }).domain) {
      entityStamp.domain = (entity as CanonicalEntity & { domain?: unknown }).domain;
    }
    if (moduleId) entityStamp.moduleId = moduleId;
    const contractRec = ((entity as CanonicalEntity & { domain?: Record<string, unknown> }).domain?.renderModeContract ?? null) as Record<string, unknown> | null;
    const contractRenderOrderRaw = contractRec?.renderOrder;
    const entityRenderOrder = typeof contractRenderOrderRaw === 'number' && Number.isFinite(contractRenderOrderRaw)
      ? contractRenderOrderRaw
      : 0;
    const groupMatrix = new THREE.Matrix4().compose(
      group.position.clone(),
      group.quaternion.clone(),
      new THREE.Vector3(1, 1, 1),
    );
    type PreparedPart = {
      partKey: string;
      primitive: string;
      geom: THREE.BufferGeometry;
      mat: THREE.MeshPhysicalMaterial;
      materialKey: string;
      offset: [number, number, number];
      rotationEuler: THREE.Euler;
      localMatrix: THREE.Matrix4;
      edgeGeometry: THREE.BufferGeometry;
      edgeGlass: boolean;
      instancingEligible: boolean;
    };
    const prepared: (PreparedPart | null)[] = parts.map((rawPart, idx) => {
      if (!rawPart || typeof rawPart !== 'object') return null;
      const part = rawPart as {
        primitive?: string;
        offsetRatio?: number[];
        sizeRatio?: number[];
        colorHex?: string;
        materialProfile?: string;
        opacity?: number;
        name?: string;
        rotationDeg?: number[];
      };
      const sr = Array.isArray(part.sizeRatio) && part.sizeRatio.length >= 3 ? part.sizeRatio : [1, 1, 1];
      const or = Array.isArray(part.offsetRatio) && part.offsetRatio.length >= 3 ? part.offsetRatio : [0, 0, 0];
      const px = Math.max(1, Math.abs(localSize[0] * Number(sr[0] || 0)));
      const py = Math.max(1, Math.abs(localSize[1] * Number(sr[1] || 0)));
      const pz = Math.max(1, Math.abs(localSize[2] * Number(sr[2] || 0)));
      const partKey = `${String(profile?.profileId || 'hardware')}|${idx}|${Math.round(px)}|${Math.round(py)}|${Math.round(pz)}`;
      const primitive = part.primitive === 'cylinder' ? 'cylinder' : 'box';
      const geom = primitive === 'cylinder'
        ? getCylinderGeometry(`cyl|${partKey}`, px, py, pz)
        : getBoxGeometry(`box|${partKey}`, px, py, pz);
      const partColor = String(part.colorHex || '');
      const materialProfile = profileFromContract(part.materialProfile);
      let baseMat: THREE.MeshPhysicalMaterial | null = null;
      let baseKey = '';
      if (isHardwareProfile && !materialProfile) {
        baseKey = `hardware-profile|${isHexColor(partColor) ? partColor : ''}`;
        baseMat = materialForHardwareProfile(
          `${String(profile?.profileId || 'hardware')}::${part.name || idx}`,
          partColor,
        );
      } else if (materialProfile && isHexColor(partColor)) {
        const glassy = materialProfile === 'glass' || materialProfile === 'mirror';
        const repeat = proceduralTextureRepeat(materialProfile, px, py, pz);
        baseKey = `object-profile|${materialProfile}|${partColor}|${glassy ? 'glass' : 'solid'}|${repeat}`;
        baseMat = materialCache.get(baseKey) ?? null;
        if (!baseMat) {
          baseMat = buildFlatExactMaterial(
            materialProfile,
            partColor,
            glassy,
            repeat,
            String(part.name || profile?.profileId || ''),
          );
          materialCache.set(baseKey, baseMat);
        }
      }
      if (!baseMat) return null;
      const mat = cloneSceneMaterial(baseMat);
      const requestedOpacity = Number(part.opacity);
      if (Number.isFinite(requestedOpacity)) {
        mat.opacity = Math.max(0.05, Math.min(1, requestedOpacity));
        mat.transparent = mat.opacity < 1;
        mat.depthWrite = mat.opacity >= 0.96;
        mat.side = mat.transparent ? THREE.DoubleSide : THREE.FrontSide;
      }
      const ox = localSize[0] * Number(or[0] || 0);
      const oy = localSize[1] * Number(or[1] || 0);
      const oz = localSize[2] * Number(or[2] || 0);
      const rotationEuler = new THREE.Euler(
        Array.isArray(part.rotationDeg) && part.rotationDeg.length >= 3 ? THREE.MathUtils.degToRad(Number(part.rotationDeg[0]) || 0) : 0,
        Array.isArray(part.rotationDeg) && part.rotationDeg.length >= 3 ? THREE.MathUtils.degToRad(Number(part.rotationDeg[1]) || 0) : 0,
        Array.isArray(part.rotationDeg) && part.rotationDeg.length >= 3 ? THREE.MathUtils.degToRad(Number(part.rotationDeg[2]) || 0) : 0,
      );
      const partMatrix = new THREE.Matrix4().compose(
        new THREE.Vector3(ox, oy, oz),
        new THREE.Quaternion().setFromEuler(rotationEuler),
        new THREE.Vector3(1, 1, 1),
      );
      const materialKey = [
        baseKey,
        mat.color.getHexString(),
        mat.roughness.toFixed(4),
        mat.metalness.toFixed(4),
        mat.emissive.getHexString(),
        mat.emissiveIntensity.toFixed(3),
        mat.opacity.toFixed(3),
        mat.map ? 'map' : '',
      ].join('|');
      return {
        partKey,
        primitive,
        geom,
        mat,
        materialKey,
        offset: [ox, oy, oz],
        rotationEuler,
        // Локальная матрица части в системе ПРОКСИ-владельца (владелец несёт
        // трансформ первой части; дети парентятся к нему).
        localMatrix: groupMatrix.clone().multiply(partMatrix),
        edgeGeometry: getEdgeGeometry(`edge|${partKey}|${primitive}`, geom, 1),
        edgeGlass: materialProfile === 'glass' || materialProfile === 'mirror',
        instancingEligible: !mat.transparent && mat.opacity >= 1 && mat.side === THREE.FrontSide,
      };
    });

    const instancingAllowed = Boolean(instancing?.active && opts?.allowInstancing);
    const instancingRecipes = instancingAllowed
      ? prepared.filter((p): p is PreparedPart => Boolean(p?.instancingEligible))
      : [];
    const preparedCount = prepared.filter(Boolean).length;
    if (instancingRecipes.length > 0 && instancingRecipes.length === preparedCount) {
      // All-or-nothing: все отрисовываемые части объекта инстансятся.
      const proxies: THREE.Object3D[] = [];
      let ownerProxy: THREE.Object3D | null = null;
      const ownerLocalInverse = new THREE.Matrix4();
      prepared.forEach((preparedPart, idx) => {
        if (!preparedPart) return;
        const isOwner = ownerProxy === null;
        const localMatrix = isOwner
          ? preparedPart.localMatrix
          : ownerLocalInverse.clone().multiply(preparedPart.localMatrix);
        if (isOwner) ownerLocalInverse.copy(preparedPart.localMatrix).invert();
        const proxy = instancing!.register({
          entityId: `${entity.id}::p${idx}`,
          ownerKey: entity.id,
          pickId: entity.id,
          geometry: preparedPart.geom,
          geometryKey: `${preparedPart.primitive}|${preparedPart.partKey}`,
          material: preparedPart.mat,
          materialKey: preparedPart.materialKey,
          matrix: localMatrix,
          renderOrder: entityRenderOrder,
          edge: {
            hardware: isHardwareProfile,
            glass: preparedPart.edgeGlass,
            cylinder: preparedPart.primitive === 'cylinder',
            edgeGeometry: preparedPart.edgeGeometry,
          },
          initiallyVisible: true,
          userData: { ...entityStamp },
        });
        proxy.visible = true;
        proxies.push(proxy);
        if (isOwner) {
          ownerProxy = proxy;
        } else {
          // Части-дети: тик собирает их матрицу из цепочки владельца, а
          // суспенд-двойники наследуют видимость владельца.
          ownerProxy!.add(proxy);
        }
        // Невидимый bounds-ребёнок на КАЖДУЮ часть: Box3.setFromObject
        // владельца даёт объединённый габарит как у классической группы.
        const boundsChild = new THREE.Mesh(preparedPart.geom, preparedPart.mat);
        boundsChild.visible = false;
        boundsChild.castShadow = false;
        boundsChild.receiveShadow = false;
        boundsChild.position.set(...preparedPart.offset);
        boundsChild.rotation.copy(preparedPart.rotationEuler);
        boundsChild.userData.analysisMarker = true;
        boundsChild.userData.instancedBoundsFor = `${entity.id}::p${idx}`;
        ownerProxy!.add(boundsChild);
      });
      const owner = ownerProxy as THREE.Object3D | null;
      if (owner) {
        // Контрактные штампы владельца как у классической группы.
        for (const [key, value] of Object.entries(group.userData)) {
          owner.userData[key] = value;
        }
        owner.userData.objectGeometryProfilePart = true;
        owner.userData.geometryProfileId = String(profile?.profileId || '');
        if (isHardwareProfile) owner.userData.hardwareProfilePart = true;
        return owner;
      }
      // Теоретически недостижимо (recipes непусты) — падение в классику.
    }

    parts.forEach((rawPart, idx) => {
      const preparedPart = prepared[idx];
      if (!preparedPart) return;
      const child = new THREE.Mesh(preparedPart.geom, preparedPart.mat);
      child.position.set(...preparedPart.offset);
      if (preparedPart.rotationEuler.x || preparedPart.rotationEuler.y || preparedPart.rotationEuler.z) {
        child.rotation.copy(preparedPart.rotationEuler);
      }
      child.castShadow = true;
      child.receiveShadow = true;
      stampHardwareObject(child);
      child.userData.objectGeometryProfilePart = true;
      child.userData.geometryProfileId = String(profile?.profileId || '');
      if (isHardwareProfile) child.userData.hardwareProfilePart = true;
      attachBasisEdgeOverlay(child, entity, `${preparedPart.partKey}|${preparedPart.primitive}`, {
        hardware: isHardwareProfile,
        glass: preparedPart.edgeGlass,
        cylinder: preparedPart.primitive === 'cylinder',
      });
      group.add(child);
    });
    return group.children.length ? group : null;
  };

  const accessoryKitsByMember = indexAccessoryArchetypeKitsByMember(
    collectAccessoryArchetypeKits(entities),
  );

  /**
   * ВОЛНА P — подмена рендера аксессуаров архетипом.
   *
   * Кит нишевого аксессуара (штанга/корзина/сушка/карго/LED) приходит из
   * бэкенда набором боксов-частей.  Владелец кита (биллинговая сущность)
   * рисует ПАРАМЕТРИЧЕСКИЙ архетип по объединённому AABB всех участников;
   * старый бокс-рендер каждого участника остаётся в графе как фолбэк для
   * техрежимов (wireframe/xray/flat — «как было») и на случай любых
   * сомнений.  Физические части плана не меняются — это render-only.
   */
  const buildEntityObject = (e: CanonicalEntity): THREE.Object3D | null => {
    const kit = accessoryKitsByMember.get(e.id);
    if (!kit) return buildLegacyEntityObject(e);
    const mode = useViewerStore.getState().renderMode;
    const legacy = buildLegacyEntityObject(e);
    if (kit.ownerEntityId !== e.id) {
      // Участник: старый рендер, скрытый в solid (архетип заменяет его).
      if (!legacy) return null;
      const outer = new THREE.Group();
      outer.userData.entityId = e.id;
      if (e.name) outer.userData.name = e.name;
      if (e.render) outer.userData.render = e.render;
      if (e.domain) outer.userData.domain = e.domain;
      const moduleId = entityModuleOwnerId(e, entitiesById);
      if (moduleId) outer.userData.moduleId = moduleId;
      outer.add(flaggedArchetypeFallback(legacy, mode));
      if (!initialFurnitureObjectVisible(e)) outer.visible = false;
      return outer;
    }
    const archetype = buildAccessoryArchetype({ role: kit.role, bounds: kit.bounds, params: kit.params });
    if (!archetype) return legacy; // фолбэк: любые сомнения → старый рендер
    const proxy = new THREE.Group();
    proxy.userData[ACCESSORY_ARCHETYPE_PROXY_FLAG] = true;
    proxy.add(archetype);
    const group = new THREE.Group();
    group.add(proxy);
    if (legacy) group.add(flaggedArchetypeFallback(legacy, mode));
    group.userData.entityId = e.id;
    if (e.name) group.userData.name = e.name;
    if (e.render) group.userData.render = e.render;
    if (e.domain) group.userData.domain = e.domain;
    const moduleId = entityModuleOwnerId(e, entitiesById);
    if (moduleId) group.userData.moduleId = moduleId;
    // Архетипные меши несут штамп владельца: пик-рейкаст и послойная
    // видимость работают с ними как с любой частью сущности.
    archetype.traverse((child) => {
      child.userData.entityId = e.id;
    });
    group.castShadow = true;
    group.receiveShadow = true;
    if (!initialFurnitureObjectVisible(e)) group.visible = false;
    initAccessoryArchetypeMode(group, mode);
    return group;
  };

  const buildLegacyEntityObject = (e: CanonicalEntity): THREE.Object3D | null => {
    const entityKind = entityObjectKind(e).toLowerCase();
    if (entityKind === 'module' && (renderedChildrenByModuleId.get(e.id) ?? 0) > 0) {
      return null;
    }
    // NOTE: viewerLayerMode-based skips USED TO live here — that meant every
    // Комната/Мебель/Техслой click
    // triggered a full scene rebuild (674 meshes + material recreation +
    // materials) which froze the main thread.  Now we build ALL eligible
    // entities once and toggle mesh.visible from a separate effect (see
    // SceneCanvas → applyLayerVisibility).  Heavy-scene perf skip stays
    // because it's a memory cap.
    const sceneEntity = new FurnitureSceneEntity(e);
    const omission = canonicalGeometryOmission(e);
    if (omission === 'service') return null;
    if (heavyScene && !sceneEntity.canReceiveSparseMesh()) return null;

    // Radius facets are the 9 box-approximation slices the backend emits to fake
    // a curved facade.  The main facade entity now renders as a true smooth arc
    // (see the radius branch below), so these render-only slices are redundant.
    // Suppress ONLY radius_facet relief — glass/sprossen/frame relief untouched.
    const domainRec = recordFrom(e.domain);
    if (omission === 'radius_facet') return null;

    const s = e.render?.size;
    const c = e.render?.center;
    if (!s || !c) return null;
    if (!Number.isFinite(s[0]) || !Number.isFinite(s[1]) || !Number.isFinite(s[2])) return null;
    if (!Number.isFinite(c[0]) || !Number.isFinite(c[1]) || !Number.isFinite(c[2])) return null;

    const sx = Math.max(s[0], 1);
    const sy = Math.max(s[1], 1);
    const sz = Math.max(s[2], 1);
    // Tiny service fragments dominate heavy models and kill frame rate.
    if (heavyScene && Math.max(sx, sy, sz) < 8) return null;

    const renderRec = recordFrom(e.render);
    const isEmissive = domainRec?.emissive === true || renderRec?.emissive === true;
    const q = e.render?.quaternion;
    const objectGeometry = buildObjectGeometryProfile(
      e,
      [c[0], c[1], c[2]],
      [sx, sy, sz],
      q ?? undefined,
      // Инстанс частей выключен для эмиссивных сущностей: на классический
      // объект вешается bloom-слой, которого у прокси нет.
      { allowInstancing: !isEmissive },
    );
    if (objectGeometry) {
      objectGeometry.castShadow = true;
      objectGeometry.receiveShadow = true;
        if (isEmissive) enableEmissiveBloomLayer(objectGeometry); // VIS-22-остаток: обе ветки создания
      objectGeometry.userData.entityId = e.id;
      if (e.name) objectGeometry.userData.name = e.name;
      if (e.render) objectGeometry.userData.render = e.render;
      if ((e as CanonicalEntity & { domain?: unknown }).domain) {
        objectGeometry.userData.domain = (e as CanonicalEntity & { domain?: unknown }).domain;
      }
      const hwMid = entityModuleOwnerId(e, entitiesById);
      if (hwMid) objectGeometry.userData.moduleId = hwMid;
      if (!initialFurnitureObjectVisible(e)) {
        objectGeometry.visible = false;
      }
      return objectGeometry;
    }

    const primitive = (e.render as { primitive?: string } | undefined)?.primitive;
    const imageUrl = String((e.render as { imageUrl?: unknown } | undefined)?.imageUrl || '').trim();
    if (primitive === 'image_plane' && imageUrl) {
      const planeKey = `image-plane|${Math.round(sx)}|${Math.round(sz)}`;
      const geom = getImagePlaneGeometry(planeKey, sx, sz);
      const opacityRaw = Number((e.render as { opacity?: unknown } | undefined)?.opacity);
      const opacity = Number.isFinite(opacityRaw) ? Math.max(0.05, Math.min(1, opacityRaw)) : 1;
      const meshMaterial = new THREE.MeshBasicMaterial({
        map: getImageTexture(imageUrl),
        transparent: opacity < 1,
        opacity,
        depthWrite: false,
        side: THREE.DoubleSide,
        toneMapped: false,
      });
      const mesh = new THREE.Mesh(geom, meshMaterial);
      mesh.position.set(c[0], c[1], c[2]);
      mesh.castShadow = false;
      mesh.receiveShadow = true;
      mesh.renderOrder = -5;
      mesh.userData.entityId = e.id;
      if (e.name) mesh.userData.name = e.name;
      if (e.render) mesh.userData.render = e.render;
      if ((e as CanonicalEntity & { domain?: unknown }).domain) {
        mesh.userData.domain = (e as CanonicalEntity & { domain?: unknown }).domain;
      }
      if (!initialFurnitureObjectVisible(e)) {
        mesh.visible = false;
      }
      return mesh;
    }

    const gKey = `${Math.round(sx)}|${Math.round(sy)}|${Math.round(sz)}`;
    const isCylinder = primitive === 'cylinder';
    // A radius facade replaces the flat box with a smoothly bowed slab.  Only
    // the two radius profiles take this branch; every other facade profile
    // (slab/shaker/glass/…) builds exactly as before.
    const facadeProfileRaw = String(domainRec?.facadeProfile ?? '').trim().toLowerCase();
    const radiusProfile: RadiusFacadeProfile | null =
      isRadiusFacadeProfile(facadeProfileRaw) && !isCylinder ? facadeProfileRaw : null;
    // Ручка — единственный hardware-класс, которому брус портит вид больше
    // всего: П-образный профиль трубы читается как ручка даже в мелком
    // масштабе. Остальная фурнитура остаётся боксами — честно по габаритам.
    const isHandleEntity = normalizeObjectKind(entityObjectKind(e)) === 'module_hardware'
      && String(domainRec?.hardwareClass ?? domainRec?.hardwareRole ?? '').trim() === 'handle';
    const geom = radiusProfile
      ? getRadiusFacadeGeometry(`radius|${radiusProfile}|${gKey}`, sx, sy, sz, radiusProfile)
      : isHandleEntity && !isCylinder
        ? getHandleSweepGeometry(`handle|${gKey}`, sx, sy, sz)
        : isCylinder ? getCylinderGeometry(`cyl|${gKey}`, sx, sy, sz) : getBoxGeometry(gKey, sx, sy, sz);

    // Hidden bounds/proxy entities still carry a material contract for
    // verification.  Visibility must be checked before that contract, or the
    // renderer turns a hidden appliance envelope (for example a hood) into a
    // visible cabinet-sized cube.
    // Fixture bounds exist for Graph-1↔Graph-2 dimensional verification only.
    // They are never renderable geometry.  Keep this semantic guard in
    // addition to visibleDefault: a legacy/bad serializer must not turn a
    // hood's verification envelope into a cabinet-sized cube.
    if (omission === 'hidden') return null;
    const surface = objectSurfaceContract(e) ?? hardwarePresentationContract(e);
    if (!surface) {
      console.error('[scene] canonical entity has no object-owned material contract', e.id, entityObjectKind(e), e.domain);
      return null;
    }
    const profile = surface.profile;
    const glassy = surface.glassy;
    const colorHex = premiumRoleTone(e, profile, surface.colorHex);
    const textureRepeat = proceduralTextureRepeat(profile, sx, sy, sz);
    const decor = resolveDecorScan(surface.article);
    // A panel whose article names a decor we cannot render must SAY so.  The
    // silent branch used to be a white board indistinguishable from a board
    // that is genuinely white, which is why the scene read as cheap without
    // anything in the data looking wrong.
    if (decor.kind === 'missing') recordDecorRefusal(e.id, decor.code, surface.article);
    // The decor belongs in the cache key: two decors sharing a base colour
    // would otherwise collide on one cached material and the second would
    // silently inherit the first one's scan.
    // VFA-FE-18: the source alpha contract keys the cache and is honored
    // after the base build — BLEND stays transparent, MASK keeps its cutoff.
    const sourceAlpha = surface.sourceAlphaMode;
    const sourceCutoff = surface.sourceAlphaCutoff;
    const exactKey = `exact|${profile}|${colorHex}|${glassy ? 'glass' : 'solid'}|tex:${textureRepeat}|${decorCacheKey(decor)}|alpha:${sourceAlpha ?? 'auto'}:${sourceCutoff ?? ''}`;
    let mat = materialCache.get(exactKey);
    if (!mat) {
      mat = buildFlatExactMaterial(profile, colorHex, glassy, textureRepeat, surface.materialName, decor);
      if (sourceAlpha === 'BLEND') {
        mat.transparent = true;
        mat.opacity = Math.min(mat.opacity, 0.72);
        mat.depthWrite = true;
      } else if (sourceAlpha === 'MASK') {
        mat.transparent = false;
        mat.alphaTest = sourceCutoff ?? 0.5;
      }
      materialCache.set(exactKey, mat);
    }

    const meshMaterial = cloneSceneMaterial(mat);
    // 29.09.2026 — премиум-глубина: шейдерный градиент по нормали грани.
    // Верх светлее (+12%), бока темнее (-8%), фронт нейтрально.
    const srcOnBefore = meshMaterial.onBeforeCompile;
    meshMaterial.onBeforeCompile = (shader, renderer) => {
      srcOnBefore?.call(meshMaterial, shader, renderer);
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <dithering_fragment>',
        `#include <dithering_fragment>
         // face-depth: normal-based lightness boost
         float _ny = abs(normalize(vNormal).y);
         float _nx = abs(normalize(vNormal).x);
         gl_FragColor.rgb *= (1.0 + _ny * 0.12 - _nx * 0.08);`
      );
    };
    const entityRole = String((e as CanonicalEntity & { role?: unknown }).role || '').trim().toLowerCase();
    const panelKind = String(domainRec?.panelKind || '').trim().toLowerCase();
    // Open cabinets are inspected mostly from above. A small neutral contrast
    // separates shelves from the white carcass without changing their BOM
    // material passport or the facade/corpus colour commands.
    if (entityRole === 'shelf' || panelKind === 'shelf') {
      meshMaterial.color.lerp(new THREE.Color('#667085'), 0.22);
      meshMaterial.roughness = Math.max(meshMaterial.roughness, 0.78);
    }
    if (isEmissive) {
      // Recognition can carry a real visible light fixture (for example an
      // under-cabinet LED strip).  It stays an ordinary editable entity while
      // its material gives the render a warm, unmistakably luminous read.
      meshMaterial.emissive.set(colorHex);
      meshMaterial.emissiveIntensity = Math.max(0.2, Math.min(5, Number(domainRec?.emissiveIntensity ?? renderRec?.emissiveIntensity) || 2.2));
      meshMaterial.toneMapped = false;
      meshMaterial.roughness = 0.28;
      meshMaterial.metalness = 0.08;
    }
    const fixtureComponent = String(domainRec?.fixtureComponent || '').trim().toLowerCase();
    const fixtureRole = String(domainRec?.fixtureRole || '').trim().toLowerCase();
    // A sink is an open basin, never two intersecting solid boxes.  Building
    // the rim and bowl from separated walls also removes worktop z-fighting.
    if (fixtureRole === 'sink' && !fixtureComponent) {
      const group = new THREE.Group();
      group.position.set(c[0], c[1] + sy / 2 + 2, c[2]);
      const rim = Math.max(8, Math.min(18, Math.min(sx, sz) * 0.045));
      const rimH = Math.max(5, Math.min(10, sy * 0.18));
      const pieces: Array<[number, number, number, number, number, number]> = [
        [sx, rimH, rim, 0, 0, -(sz - rim) / 2], [sx, rimH, rim, 0, 0, (sz - rim) / 2],
        [rim, rimH, Math.max(1, sz - 2 * rim), -(sx - rim) / 2, 0, 0],
        [rim, rimH, Math.max(1, sz - 2 * rim), (sx - rim) / 2, 0, 0],
      ];
      for (const [w, h, d, x, y, z] of pieces) {
        const part = new THREE.Mesh(getBoxGeometry(`sink-rim|${w}|${h}|${d}`, w, h, d), cloneSceneMaterial(meshMaterial));
        part.position.set(x, y, z); part.castShadow = true; part.userData.entityId = e.id; group.add(part);
      }
      group.userData.entityId = e.id; group.userData.domain = e.domain;
      return group;
    }
    if (fixtureComponent === 'bowl') {
      const group = new THREE.Group();
      group.position.set(c[0], c[1], c[2]);
      const wall = Math.max(8, Math.min(16, Math.min(sx, sz) * 0.05));
      const bottom = Math.max(8, Math.min(14, sy * 0.12));
      const pieces: Array<[number, number, number, number, number, number]> = [
        [Math.max(1, sx - 2 * wall), bottom, Math.max(1, sz - 2 * wall), 0, -sy / 2 + bottom / 2, 0],
        [wall, sy, sz, -(sx - wall) / 2, 0, 0], [wall, sy, sz, (sx - wall) / 2, 0, 0],
        [Math.max(1, sx - 2 * wall), sy, wall, 0, 0, -(sz - wall) / 2],
        [Math.max(1, sx - 2 * wall), sy, wall, 0, 0, (sz - wall) / 2],
      ];
      for (const [w, h, d, x, y, z] of pieces) {
        const partMat = cloneSceneMaterial(meshMaterial); partMat.side = THREE.DoubleSide;
        const part = new THREE.Mesh(getBoxGeometry(`sink-bowl|${w}|${h}|${d}`, w, h, d), partMat);
        part.position.set(x, y, z); part.castShadow = true; part.userData.entityId = e.id; group.add(part);
      }
      group.userData.entityId = e.id; group.userData.domain = e.domain;
      return group;
    }
    if (fixtureComponent === 'burner') {
      const ringSize = Math.max(20, Math.min(sx, sz) * 0.38);
      const ringGeom = new THREE.TorusGeometry(ringSize, Math.max(2, ringSize * 0.045), 8, heavyScene ? 24 : 40);
      ringGeom.rotateX(Math.PI / 2);
      const ringMat = new THREE.MeshStandardMaterial({ color: '#a8b0ba', metalness: 0.58, roughness: 0.32 });
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.position.set(c[0], c[1] + sy / 2 + 2.5, c[2]);
      ring.castShadow = false; ring.userData.entityId = e.id; ring.userData.domain = e.domain;
      return ring;
    }
    // PERF (mesh-instancing): массовая группа — панели/полки/фасады с
    // повторяющимися (геометрия, материал) — рисуется одним InstancedMesh на
    // бакет. Прокси-объект сохраняет весь контракт сущности: raycast-выделение
    // резолвится по instanceId (useSelection), тумблеры/скрытие работают через
    // proxy.visible → нулевая матрица слота, анимации двигают прокси как
    // обычный Object3D. Всё, что ведёт себя нестандартно (радиус-фасады,
    // ручки, цилиндры, эмиссив, подсветка, стены, контракты режимов,
    // прозрачность) — остаётся на классическом пути.
    const taskLighting = underCabinetLightingVisualContract(domainRec, renderRec);
    if (
      instancing?.active
      && !radiusProfile
      && !isHandleEntity
      && !isCylinder
      && !isEmissive
      && !taskLighting
      && !isApartmentWallDomain(domainRec ?? {})
      && renderModeContractIsInstancingSafe(domainRec?.renderModeContract)
      && !meshMaterial.transparent
      && meshMaterial.opacity >= 1
      && meshMaterial.side === THREE.FrontSide
    ) {
      const edgeGeometry = getEdgeGeometry(`edge|box|${gKey}`, geom, 1);
      const instancingQuat = new THREE.Quaternion();
      if (q && q.length >= 4) instancingQuat.set(q[0], q[1], q[2], q[3]).normalize();
      const glassPlateLift = fixtureComponent === 'glass_plate' ? Math.max(5, sy * 0.2) : 0;
      const instancingMatrix = new THREE.Matrix4().compose(
        new THREE.Vector3(c[0], c[1] + glassPlateLift, c[2]),
        instancingQuat,
        new THREE.Vector3(1, 1, 1),
      );
      // Бакет = (геометрия, финальный материал, renderOrder). Финальные
      // свойства важны: полки подкрашиваются, emissive/opacity мутируются ДО
      // этой точки — члены бакета обязаны быть неотличимы и по материалу.
      const instancingMaterialKey = [
        exactKey,
        meshMaterial.color.getHexString(),
        meshMaterial.roughness.toFixed(4),
        meshMaterial.metalness.toFixed(4),
        meshMaterial.emissive.getHexString(),
        meshMaterial.emissiveIntensity.toFixed(3),
        meshMaterial.opacity.toFixed(3),
        meshMaterial.envMapIntensity?.toFixed(3) ?? '',
        meshMaterial.map ? 'map' : '',
      ].join('|');
      const instancingMid = entityModuleOwnerId(e, entitiesById);
      const contractRec = domainRec?.renderModeContract as Record<string, unknown> | null | undefined;
      const contractRenderOrderRaw = contractRec?.renderOrder;
      const instancingRenderOrder = typeof contractRenderOrderRaw === 'number' && Number.isFinite(contractRenderOrderRaw)
        ? contractRenderOrderRaw
        : 0;
      const instancingUserData: Record<string, unknown> = { entityId: e.id };
      if (e.name) instancingUserData.name = e.name;
      if (e.render) instancingUserData.render = e.render;
      if ((e as CanonicalEntity & { domain?: unknown }).domain) {
        instancingUserData.domain = (e as CanonicalEntity & { domain?: unknown }).domain;
      }
      if (instancingMid) instancingUserData.moduleId = instancingMid;
      const instancingInitiallyVisible = initialFurnitureObjectVisible(e);
      const proxy = instancing.register({
        entityId: e.id,
        geometry: geom,
        geometryKey: `box|${gKey}`,
        material: meshMaterial,
        materialKey: instancingMaterialKey,
        matrix: instancingMatrix,
        renderOrder: instancingRenderOrder,
        edge: {
          hardware: false,
          glass: glassy || profile === 'glass',
          cylinder: false,
          edgeGeometry,
        },
        initiallyVisible: instancingInitiallyVisible,
        userData: instancingUserData,
      });
      proxy.visible = instancingInitiallyVisible;
      return proxy;
    }
    const mesh = new THREE.Mesh(geom, meshMaterial);
    if (isEmissive) enableEmissiveBloomLayer(mesh); // VIS-22-остаток: селективный bloom
    mesh.position.set(c[0], c[1], c[2]);
    if (fixtureComponent === 'glass_plate') mesh.position.y += Math.max(5, sy * 0.2);
    if (taskLighting) {
      new UnderCabinetLightingVisual(e.id).attach(mesh, [sx, sy, sz], taskLighting);
    }
    if (q && q.length >= 4) {
      mesh.quaternion.set(q[0], q[1], q[2], q[3]).normalize();
    }
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.entityId = e.id;
    if (e.name) mesh.userData.name = e.name;
    if (e.render) mesh.userData.render = e.render;
    if ((e as CanonicalEntity & { domain?: unknown }).domain) {
      mesh.userData.domain = (e as CanonicalEntity & { domain?: unknown }).domain;
    }
    // moduleId is stamped on composed apartment entities (parts, hardware) —
    // used by the layer-visibility pass to bundle a module's interior with
    // its anchor when toggling Комната / Мебель / Техслой.
    const mid = entityModuleOwnerId(e, entitiesById);
    if (mid) mesh.userData.moduleId = mid;
    if (!initialFurnitureObjectVisible(e)) {
      mesh.visible = false;
    }
    attachBasisEdgeOverlay(
      mesh,
      e,
      `${radiusProfile ? `radius|${radiusProfile}` : isHandleEntity ? 'handle' : isCylinder ? 'cyl' : 'box'}|${gKey}`,
      {
        glass: glassy || profile === 'glass',
        cylinder: isCylinder,
        // Only outline the perimeter of the curved slab, not every arc facet.
        edgeThresholdDeg: radiusProfile ? 30 : undefined,
      },
    );
    return mesh;
  };

  const renderedChildModuleIds = new Set<string>();
  for (const [moduleId, count] of renderedChildrenByModuleId) {
    if (count > 0) renderedChildModuleIds.add(moduleId);
  }

  return {
    entities,
    entitiesById,
    renderedChildModuleIds,
    maxCanonicalMeshes,
    accessoryArchetypeMemberIds: new Set(accessoryKitsByMember.keys()),
    buildEntityObject,
  };
}

/**
 * Обёртка старого рендера части кита: узел с меткой режимной видимости
 * (виден во всех режимах, кроме solid).  Метка живёт на ПРОМЕЖУТОЧНОЙ
 * группе без entityId/domain — послойная видимость пишет .visible только
 * на верхний уровень сущности, режимная — на этот узел; уровни не спорят.
 */
function flaggedArchetypeFallback(legacy: THREE.Object3D, mode: string): THREE.Group {
  const wrap = new THREE.Group();
  wrap.userData[ACCESSORY_ARCHETYPE_FALLBACK_FLAG] = true;
  wrap.visible = mode !== 'solid';
  wrap.add(legacy);
  return wrap;
}

/**
 * Build a detached object tree from the exact same canonical render contract
 * used by SceneCanvas.  Catalog thumbnails and drag ghosts use this function;
 * they must never approximate a module with its envelope when real children
 * exist.  The caller owns the returned GPU resources.
 */
export function buildCanonicalEntityPreviewGroup(entities: CanonicalEntity[]): THREE.Group {
  const bundle = {
    canonicalScene: {
      schema: 'holzcom.canonical_scene.v1',
      units: 'mm',
      entities,
    },
  } as UniversalBundle;
  const builder = createCanonicalBuilder(bundle);
  const group = new THREE.Group();
  group.name = 'canonical-module-preview';
  let count = 0;
  for (const entity of builder.entities) {
    const object = builder.buildEntityObject(entity);
    if (!object) continue;
    try {
      assertCanonicalSceneCapacity(count, builder.maxCanonicalMeshes);
    } catch (error) {
      disposeObject(object);
      disposeObject(group);
      throw error;
    }
    object.name = entity.id;
    group.add(object);
    count += 1;
  }
  return group;
}

export function disposeCanonicalEntityPreviewGroup(group: THREE.Object3D): void {
  disposeObject(group);
}

/**
 * Snapshot of the last committed scene build.  Used exclusively by the
 * incremental fast path to (a) decide whether a rebuild is even possible and
 * (b) locate the Object3D to replace for each changed entity.
 */
type PrevBuild = {
  modelId: string;
  hashById: Map<string, string>;
  index: Map<string, THREE.Object3D>;
  rootGroup: THREE.Group;
  renderedChildModuleIds: Set<string>;
  /** Реестр инстанс-батчей этой сборки (null — сборка без инстансинга). */
  registry: InstancedBatchRegistry | null;
};

type IncrementalResult =
  | { outcome: 'fallback' }
  | { outcome: 'noop'; prevBuild: PrevBuild }
  | { outcome: 'applied'; prevBuild: PrevBuild; state: LoaderState };

function setsEqual(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Shared-resource reference counting (fast path only)
// ---------------------------------------------------------------------------
//
// Within one build, geometries / textures are shared across meshes via the
// per-build caches inside createCanonicalBuilder (one BoxGeometry per rounded
// dimension key; one procedural texture per material key, referenced by every
// cloned material).  Every object built by ANY path is `retainSharedResources`d
// exactly once per mesh-reference; every object torn down on the FAST path is
// `releaseSharedResources`d exactly once per mesh-reference.  A resource is
// disposed the moment its count provably reaches 0 — no sooner (that would be
// use-after-dispose on a reused object) and no later (that was the old leak).
//
// The FULL teardown path (disposeObject, used on model switch / fallback /
// content wipe) frees everything wholesale, so it does NOT decrement — instead
// the counter is `clear()`ed at those points so counts never go stale.
const sharedResources = new ResourceRefCounter();

// Material texture slots that may hold a shared THREE.Texture.  Matches the
// slots written by buildFlatExactMaterial / the image-plane path.
const MATERIAL_TEXTURE_SLOTS = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'emissiveMap',
  'bumpMap',
  'displacementMap',
  'alphaMap',
  'envMap',
] as const;

function forEachDrawable(obj: THREE.Object3D, fn: (d: THREE.Mesh | THREE.LineSegments) => void) {
  obj.traverse((o) => {
    const drawable = o as THREE.Mesh | THREE.LineSegments;
    if ((drawable as THREE.Mesh).isMesh || (drawable as THREE.LineSegments).isLineSegments) {
      fn(drawable);
    }
  });
}

function materialTextures(mat: THREE.Material): THREE.Texture[] {
  const out: THREE.Texture[] = [];
  const record = mat as unknown as Record<string, unknown>;
  for (const slot of MATERIAL_TEXTURE_SLOTS) {
    const tex = record[slot];
    if (tex && (tex as THREE.Texture).isTexture) out.push(tex as THREE.Texture);
  }
  return out;
}

/**
 * Register one live reference to every shared resource in `obj` (its geometry,
 * each material, and each material texture).  Called once per committed object
 * from BOTH the full and the incremental build paths, so the counts always
 * reflect the true number of live meshes referencing each resource.
 */
function retainSharedResources(obj: THREE.Object3D): void {
  forEachDrawable(obj, (drawable) => {
    const geom = drawable.geometry as THREE.BufferGeometry | undefined;
    if (geom) sharedResources.retain(geom);
    const mat = drawable.material;
    const mats = Array.isArray(mat) ? mat : mat ? [mat] : [];
    for (const m of mats) {
      sharedResources.retain(m);
      for (const tex of materialTextures(m)) sharedResources.retain(tex);
    }
  });
}

/**
 * Detach `obj` from its parent and dispose each of its shared resources ONLY
 * when reference counting proves no live mesh references it any more.
 *
 * SAFETY: `release` returns true exclusively at a provable zero.  Anything not
 * tracked (foreign meshes injected by other hooks, already-evicted resources,
 * unexpected ownership) yields `false` and is left completely untouched — the
 * worst case degrades to the old bounded leak, never a use-after-dispose that
 * would blank out a reused object.  Textures are released independently of
 * their (unique) material because a procedural texture is shared across every
 * cloned material of the same passport.
 */
function releaseSharedResources(obj: THREE.Object3D): void {
  forEachDrawable(obj, (drawable) => {
    const mat = drawable.material;
    const mats = Array.isArray(mat) ? mat : mat ? [mat] : [];
    for (const m of mats) {
      for (const tex of materialTextures(m)) {
        if (sharedResources.release(tex)) tex.dispose();
      }
      if (sharedResources.release(m)) m.dispose();
    }
    const geom = drawable.geometry as THREE.BufferGeometry | undefined;
    if (geom && sharedResources.release(geom)) {
      (geom as unknown as { disposeBoundsTree?: () => void }).disposeBoundsTree?.();
      geom.dispose();
    }
  });
  obj.parent?.remove(obj);
}

/**
 * Attempt a partial rebuild that reuses unchanged Object3D and rebuilds only
 * the entities whose render hash changed.  Returns 'fallback' on ANY condition
 * that isn't provably safe, so the caller runs the full byte-identical path.
 *
 * Never fits the camera or emits bounds — the current view is preserved.
 */
function tryIncrementalRebuild(
  ctx: ThreeCtx,
  modelId: string,
  bundle: UniversalBundle,
  prev: PrevBuild | null,
  nextEntities: CanonicalEntity[],
): IncrementalResult {
  // Need a prior build of the SAME model whose root is still attached.
  if (!prev || prev.modelId !== modelId) return { outcome: 'fallback' };
  const rootGroup = prev.rootGroup;
  if (!rootGroup || rootGroup.parent !== ctx.content) return { outcome: 'fallback' };

  const diff = diffEntitySets(prev.hashById, nextEntities);
  // Added / removed entities change scene membership (and could change the
  // heavy-scene threshold or module suppression) → full path.
  if (!diff.sameIdSet) return { outcome: 'fallback' };

  // Full-context builder: heavy-scene threshold, module-owner resolution and
  // envelope suppression are all computed from the WHOLE next bundle, so any
  // entity rebuilt here is produced identically to a full rebuild.
  const builder = createCanonicalBuilder(bundle, prev.registry);

  // An entity that was previously non-rendering may become renderable while
  // the id set stays unchanged. Fall back before mutating the live graph when
  // that worst-case growth could cross the full builder's safety boundary.
  const potentialNewObjects = diff.rebuildIds.filter((id) => !prev.index.has(id)).length;
  if (incrementalSceneMayExceedCapacity(prev.index.size, potentialNewObjects, builder.maxCanonicalMeshes)) {
    return { outcome: 'fallback' };
  }

  // ВОЛНА P: архетип аксессуара строится по объединённому AABB всего кита —
  // правка одного участника меняет картинку остальных участников тоже.
  // Per-entity rebuild этого не выражает → полный путь.
  if (diff.rebuildIds.some((id) => builder.accessoryArchetypeMemberIds.has(id))) {
    return { outcome: 'fallback' };
  }

  // If the set of modules whose envelope is suppressed by rendered children
  // changed, a child's render-presence flip may add/remove a module envelope —
  // a cross-entity effect a per-entity rebuild cannot express.  Full path.
  if (!setsEqual(builder.renderedChildModuleIds, prev.renderedChildModuleIds)) {
    return { outcome: 'fallback' };
  }

  const nextHashById = buildHashMap(nextEntities);

  if (diff.rebuildIds.length === 0) {
    // Scene is visually identical — keep graph, camera and React state as-is.
    return {
      outcome: 'noop',
      prevBuild: {
        modelId,
        hashById: nextHashById,
        index: prev.index,
        rootGroup,
        renderedChildModuleIds: builder.renderedChildModuleIds,
        registry: prev.registry,
      },
    };
  }

  // Reparent safety: any changed entity that currently has an object must have
  // it parented directly under our root group.  If module-editing reparented
  // it under a transformed anchor, a fresh world-space object would be placed
  // wrong → full path.
  for (const id of diff.rebuildIds) {
    const oldObj = prev.index.get(id);
    if (oldObj && oldObj.parent !== rootGroup) return { outcome: 'fallback' };
  }

  const nextIndex = new Map(prev.index);
  const freshMeshes: THREE.Mesh[] = [];

  for (const id of diff.rebuildIds) {
    const entity = builder.entitiesById.get(id);
    if (!entity) return { outcome: 'fallback' }; // defensive; id came from nextEntities

    // Инстанс-слот старого объекта обязан освободиться ДО сборки нового (новый
    // регистрируется в тот же реестр). Ресурсы старого прокси (bounds-ребёнок)
    // освобождает releaseSharedResources ниже — реестр их не трогает.
    prev.registry?.release(id);
    const newObj = builder.buildEntityObject(entity);
    const oldObj = prev.index.get(id);
    // Retain the new object's resources BEFORE releasing the old one's, so a
    // resource shared between them (same dims/material across a rebuild) never
    // transiently hits 0 and gets disposed out from under the replacement.
    if (newObj) retainSharedResources(newObj);
    if (oldObj) releaseSharedResources(oldObj);

    if (newObj) {
      rootGroup.add(newObj);
      nextIndex.set(id, newObj);
      newObj.traverse((n) => {
        if ((n as THREE.Mesh).isMesh) freshMeshes.push(n as THREE.Mesh);
      });
    } else {
      // Entity now renders nothing — matches a full rebuild where it'd be absent.
      nextIndex.delete(id);
    }
  }

  buildMeshBvhs(freshMeshes);

  return {
    outcome: 'applied',
    state: { status: 'ready', modelId, entityIndex: nextIndex },
    prevBuild: {
      modelId,
      hashById: nextHashById,
      index: nextIndex,
      rootGroup,
      renderedChildModuleIds: builder.renderedChildModuleIds,
      registry: prev.registry,
    },
  };
}

function buildFromCanonical(bundle: UniversalBundle, instancing = false) {
  const root = new THREE.Group();
  root.name = 'canonical-scene';
  const index = new Map<string, THREE.Object3D>();
  // PERF (mesh-instancing): реестр живёт только у основной сборки сцены.
  // Превью-группы (миниатюры/призраки) строятся классическим путём — прокси
  // без реестра ничего не рисует.
  const registry = instancing
    ? new InstancedBatchRegistry(root, {
        retain: retainSharedResources,
        releaseObj: releaseSharedResources,
      })
    : null;
  const builder = createCanonicalBuilder(bundle, registry);

  let emitted = 0;
  for (const e of builder.entities) {
    const obj = builder.buildEntityObject(e);
    if (!obj) continue;
    try {
      assertCanonicalSceneCapacity(emitted, builder.maxCanonicalMeshes);
    } catch (error) {
      disposeObject(obj);
      registry?.dispose();
      disposeObject(root);
      throw error;
    }
    root.add(obj);
    index.set(e.id, obj);
    emitted += 1;
  }

  // Build BVH on canonical meshes too so click picking is fast.
  const canonicalMeshes: THREE.Mesh[] = [];
  root.traverse((n) => {
    if ((n as THREE.Mesh).isMesh) canonicalMeshes.push(n as THREE.Mesh);
  });
  buildMeshBvhs(canonicalMeshes);
  return { group: root, index, renderedChildModuleIds: builder.renderedChildModuleIds, registry };
}

// Dispose a material and every THREE.Texture it references. Procedural
// CanvasTextures (map/normalMap/…) and loaded textures are per-load (not shared
// across loads), so releasing them here prevents a GPU texture leak on model
// switch. Geometry/BVH disposal is handled separately by the caller.
function disposeMaterial(mat: THREE.Material) {
  for (const key of [
    'map',
    'normalMap',
    'roughnessMap',
    'metalnessMap',
    'aoMap',
    'emissiveMap',
    'bumpMap',
    'displacementMap',
    'alphaMap',
    'envMap',
  ] as const) {
    const tex = (mat as unknown as Record<string, unknown>)[key];
    if (tex && (tex as THREE.Texture).isTexture) (tex as THREE.Texture).dispose();
  }
  mat.dispose();
}

function disposeObject(obj: THREE.Object3D) {
  obj.traverse((o) => {
    const drawable = o as THREE.Mesh | THREE.LineSegments;
    if ((drawable as THREE.Mesh).isMesh || (drawable as THREE.LineSegments).isLineSegments) {
      // Release BVH structure before geometry.dispose so the typed arrays
      // backing the tree are freed promptly on model switch.
      (drawable.geometry as any)?.disposeBoundsTree?.();
      drawable.geometry?.dispose();
      const mat = drawable.material;
      if (Array.isArray(mat)) mat.forEach((x) => disposeMaterial(x));
      else if (mat) disposeMaterial(mat);
    }
  });
  obj.parent?.remove(obj);
}
